import { defineStore } from 'pinia'
import { computed, nextTick, onScopeDispose, ref, shallowRef, watch } from 'vue'

import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import { evaluateLayerPoseAtGlobalTime } from '@/animation/evaluateLayerPoseAtGlobalTime'
import { PlaybackController } from '@/animation/playback/PlaybackController'
import {
  clampClipStartNonOverlapping,
  clampResizeClipLeft,
  clampResizeClipRight,
  findClipContainingGlobalTime,
  MIN_CLIP_DURATION_SEC,
  proposeClipInGapAtTimeWithinWindow,
} from '@/animation/timeline/clipConstraints'
import {
  createTwoEndpointKeyframes,
  DEFAULT_NEW_CLIP_DURATION_SEC,
  getPoseFromTouchingPreviousClipEnd,
  isPlayheadInsideClipInterior,
  normalizeClipToEndpointPair,
  resetClipEndpointsToDefaultPose,
  syncAdjacentClipBoundaries,
  writeLayerPoseToClipBoundariesAtGlobalTime,
  type LayerPose,
} from '@/animation/timeline/clipEndpoints'
import { buildRulerTickSetMinMajorStep, type RulerTickSet } from '@/animation/timeline/rulerTicks'
import { playheadSnapThresholdSec } from '@/animation/timeline/playheadSnap'
import { snapSecondsToStep } from '@/animation/timeline/snap'
import type { AnimationClip } from '@/types/animationClip'
import type { EasingPreset } from '@/types/easingPreset'
import { isEasingPreset } from '@/types/easingPreset'
import type { AnimationLayer, Vec3 } from '@/types/animationLayer'
import { defaultScreenMaterialFields, defaultVisibilityFields } from '@/types/animationLayer'

import {
  GRADIENT_PRESET_LIST,
  type GradientStop,
} from '@/constants/gradientPresets'
import {
  readStoredScreenImageTransitionModeDefault,
  writeStoredScreenImageTransitionModeDefault,
  type ScreenImageTransitionModeId,
} from '@/constants/screenImageTransition'
import {
  clampViewportAmbientIntensityScale,
  DEFAULT_LIGHTING_PRESET_ID,
  type LightingPresetId,
  isLightingPresetId,
  VIEWPORT_AMBIENT_INTENSITY_SCALE_DEFAULT,
} from '@/constants/lightingPresets'
import {
  BG_COLOR_PRESETS,
  getExportDimensions,
  MOCKUP_DEVICE_TEMPLATES,
  type MockupCategoryId,
  type MockupDeviceTemplate,
  type RatioPresetId,
  type ResolutionPresetId,
  RESOLUTION_PRESETS,
  RATIO_PRESET_DIMENSIONS,
  RATIO_PRESETS,
  scalePreviewToExportDimensions,
} from '@/constants/mockupDevices'
import {
  clearEditorDraft,
  createEditorDraftAutoSaveController,
  editorDraftBackgroundAssetKey,
  editorDraftLayerLegacyImageAssetKey,
  editorDraftLayerScreenImageAssetKey,
  editorDraftLayerVideoAssetKey,
  type EditorDraftAssetInput,
  type EditorDraftAssetRecord,
} from '@/project/editorProjectDraftCache'
import {
  PROJECT_SNAPSHOT_VERSION,
  animationLayerFromJson,
  animationLayerToJson,
  normalizeProjectSnapshot,
  type ProjectSnapshot,
} from '@/project/projectSnapshot'
import { i18n } from '@/i18n'
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  type UiLocale,
} from '@/i18n/schema'

import { uiLocaleToEditorRouterPath } from '@/router/editorUrlLocale'
import { ViewportSceneAdapter } from '@/rendering/export/adapters/ViewportSceneAdapter'
import {
  DEFAULT_VIDEO_EXPORT_FORMAT,
  DEFAULT_VIDEO_EXPORT_FPS,
  INTERNAL_EXPORT_QUALITY,
  type VideoExportFormatId,
  type VideoExportFps,
} from '@/rendering/export/exportPresets'
import { runOfflineRenderPipeline } from '@/rendering/export/OfflineRenderOrchestrator'
import type { ExportRenderConfig } from '@/rendering/export/types'
import {
  resolveVideoEncoder,
  VideoExportNotSupportedError,
} from '@/rendering/export/VideoExportController'
import { getViewportExportApi } from '@/rendering/viewportExportBridge'
import { triggerBlobDownload } from '@/utils/downloadBlob'
import {
  isRasterImageScreenMaterialFile,
  isScreenMaterialFileAcceptedForLayer,
  isVideoMp4ScreenMaterialFile,
} from '@/utils/mockupScreenMaterialFile'
import {
  isRejectedAnimatedRasterScreenFile,
  prepareScreenRasterFileForIngest,
} from '@/utils/screenImageUploadValidation'
import { normalizeHex7, toCssLinearGradient } from '@/utils/gradient'
import { postExportPreflight } from '@/api/exportPreflight'
import { useEditorAuth } from '@/composables/useEditorAuth'
import { useEditorAuthDialog } from '@/composables/useEditorAuthDialog'
import { resolveExportIncludeWatermark } from '@/config/imockupBuildFlags'
import { MAX_SCREEN_IMAGE_COUNT } from '@/config/mockupScreenMediaLimits'
import { requestScreenMediaOverwriteConfirm } from '@/composables/screenMediaOverwriteConfirm'
import { createCompactRandomId } from '../../../shared/utils/randomUuid'

import { deepCloneLayer, layerEditStateEquals } from '@/editor/history/cloneUtils'
import * as EditorHistCmd from '@/editor/history/commands'
import { setMockupHistoryBridge, type MockupHistoryBridge } from '@/editor/history/mockupEditorBridge'
import { getMockupEditorHistory } from '@/editor/history/mockupHistorySingleton'
import type { EditorHistoryCommand } from '@/editor/history/types'
import type { ScreenMaterialSnapshot } from '@/editor/history/commands/layerPropertyCommands'

/**
 * 全屏浮层提示内容（与 {@link showEditorLayerToast} 配套）。
 */
export type EditorLayerToastState = {
  title: string
  body: string
  variant: 'error' | 'info'
}

export type InspectorTabId = 'basic' | 'controls'

/** 重新导出给组件使用（保持外部 import 稳定）。 */
export type ExportFormatId = VideoExportFormatId
export type ExportFpsId = VideoExportFps
export type BackgroundModeId = 'color' | 'gradient' | 'transparent' | 'image'
export type WorkspaceMode = 'default' | 'edit'
export type CopiedKeyframePose = {
  position: Vec3
  rotationDeg: Vec3
  scale: Vec3
  /** 复制自关键帧；粘贴到非图片平面时忽略 */
  materialOpacity: number
}

export type ViewportHandlers = {
  zoomIn: () => void
  zoomOut: () => void
  resetView: () => void
  toggleAutoRotate: () => void
}

/** 视口底部工具：对当前选中图层做位移 / 旋转 / 缩放（与属性面板同一数据源）。 */
export type ViewportTransformToolId = 'move' | 'rotate' | 'scale'

/**
 * 首页编辑器 UI 状态与占位动作；Three 视口通过 `setViewportHandlers` 挂载控制回调。
 */
function createStopId(): string {
  return createCompactRandomId('gs')
}

function createLayerId(): string {
  return createCompactRandomId('layer')
}

function createClipId(): string {
  return createCompactRandomId('clip')
}

function createKeyframeId(): string {
  return createCompactRandomId('kf')
}

function cloneVec3(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z }
}

function defaultVec3(): Vec3 {
  return { x: 0, y: 0, z: 0 }
}

function defaultScaleVec3(): Vec3 {
  return { x: 1, y: 1, z: 1 }
}

/**
 * 归一化图层可见区间，保证落在 `[0,duration]` 且满足 `start <= end`。
 *
 * @param startSec - 起点（秒）
 * @param endSec - 终点（秒）
 * @param durationSec - 总时长（秒）
 */
function normalizeVisibilityWindow(
  startSec: number,
  endSec: number,
  durationSec: number,
): { startSec: number; endSec: number } {
  const max = Number.isFinite(durationSec) ? Math.max(0, durationSec) : 0
  const rawStart = Number.isFinite(startSec) ? startSec : 0
  const rawEnd = Number.isFinite(endSec) ? endSec : max
  const a = Math.min(max, Math.max(0, rawStart))
  const b = Math.min(max, Math.max(0, rawEnd))
  return a <= b ? { startSec: a, endSec: b } : { startSec: b, endSec: a }
}

/**
 * @param layer - 运行时图层
 * @returns 屏幕材质字段快照（用于撤销命令）
 */
function screenMaterialSnapshotFromLayer(layer: AnimationLayer): ScreenMaterialSnapshot {
  return {
    fileName: layer.screenMaterialFileName,
    objectUrl: layer.screenTextureSrc,
    mediaType: layer.screenMediaType,
    offsetSec: layer.screenVideoTimelineOffsetSec,
    screenImages: layer.screenImages.map((it) => ({
      id: it.id,
      fileName: it.fileName,
      objectUrl: it.textureSrc,
    })),
  }
}

function createScreenImageStackItemId(): string {
  return createCompactRandomId('si')
}

function revokeAllScreenImageStackUrls(layer: AnimationLayer): void {
  for (const it of layer.screenImages) {
    if (it.textureSrc) {
      try {
        URL.revokeObjectURL(it.textureSrc)
      } catch {
        /* ignore */
      }
      it.textureSrc = null
    }
  }
  layer.screenImages = []
}

function revokeAllLayerScreenBlobUrls(layer: AnimationLayer): void {
  if (layer.screenTextureSrc) {
    try {
      URL.revokeObjectURL(layer.screenTextureSrc)
    } catch {
      /* ignore */
    }
  }
  for (const it of layer.screenImages) {
    if (it.textureSrc) {
      try {
        URL.revokeObjectURL(it.textureSrc)
      } catch {
        /* ignore */
      }
    }
  }
}

function scrubScreenImageIdFromLayerClips(layer: AnimationLayer, removedId: string): void {
  for (const c of layer.clips) {
    for (const k of c.keyframes) {
      if (k.screenImageId === removedId) k.screenImageId = undefined
    }
  }
}

function seedToGradientStops(
  seeds: readonly { readonly hex: string; readonly position: number }[],
): GradientStop[] {
  return seeds.map((s) => ({
    id: createStopId(),
    hex: normalizeHex7(s.hex),
    position: Math.min(100, Math.max(0, s.position)),
  }))
}

/**
 * 按当前色标顺序（位置升序，同位按 id）将 `position` 均匀映射到 `[0,100]`。
 */
function redistributeGradientStopsEvenly(stops: GradientStop[]): void {
  const n = stops.length
  if (n < 2) return
  const sorted = [...stops].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))
  for (let i = 0; i < n; i += 1) {
    const target = sorted[i]!
    const p = (i / (n - 1)) * 100
    const ref = stops.find((s) => s.id === target.id)
    if (ref) ref.position = Math.round(p * 100) / 100
  }
}

export const useMockupEditorStore = defineStore('mockupEditor', () => {
  const availableLocales: readonly UiLocale[] = SUPPORTED_LOCALES
  const locale = ref<UiLocale>(DEFAULT_LOCALE)
  const workspaceMode = ref<WorkspaceMode>('default')

  const selectedTemplateId = ref<string | null>(
    MOCKUP_DEVICE_TEMPLATES.find((d) => d.category === 'phone')?.id ??
      MOCKUP_DEVICE_TEMPLATES[0]?.id ??
      null,
  )
  const mockupCategory = ref<MockupCategoryId>('phone')
  const deviceSearchQuery = ref('')
  const sceneWidth = ref(1920)
  const sceneHeight = ref(1080)
  const activeResolutionPreset = ref<ResolutionPresetId>('1080P')
  const activeRatioPreset = ref<RatioPresetId | null>('16:9')
  const backgroundMode = ref<BackgroundModeId>('color')
  const backgroundHex = ref('#ffffff')
  /** 背景图片原始文件名（用于 UI 展示；持久化到工程 JSON） */
  const backgroundImageFileName = ref<string | null>(null)
  /** 背景图片 blob URL（运行时；加载工程时需要用户重选文件） */
  const backgroundImageSrc = ref<string | null>(null)
  const defaultPreset = GRADIENT_PRESET_LIST.find((p) => p.id === 'studio-soft') ?? GRADIENT_PRESET_LIST[0]!
  const gradientStops = ref<GradientStop[]>(seedToGradientStops(defaultPreset.stops))
  const activeGradientPresetId = ref<string | null>('studio-soft')
  /** 视口 Three 场景灯光 / HDR IBL 预设 */
  const lightingPresetId = ref<LightingPresetId>(DEFAULT_LIGHTING_PRESET_ID)
  /**
   * 半球光 + `scene.environmentIntensity` 的全局倍率（与灯光预设相乘，便于整体提亮/压暗环境）。
   */
  const viewportAmbientIntensityScale = ref(VIEWPORT_AMBIENT_INTENSITY_SCALE_DEFAULT)
  const currentTimeSeconds = ref(0)
  const totalDurationSeconds = ref(10)
  /**
   * 时间轴区域显示颗粒度：`0` 表示横向适配整段总时长（总秒一览）；越大则时间格放大、轨道越宽，超出可视区可左右滑动。
   */
  const timelineGranularity = ref(0)
  /** 时间轴轨道可视宽度（像素），用于限制最大缩放（约 300px/秒） */
  const timelineTrackClientWidthPx = ref(880)
  const isPlaying = ref(false)
  const inspectorTab = ref<InspectorTabId>('basic')
  /** 时间轴动画图层（与 Mockup Controls 绑定） */
  const animationLayers = ref<AnimationLayer[]>([])
  /** 时间轴上选中的动画片段 id（与 `selectedLayerId` 联动） */
  const selectedClipId = ref<string | null>(null)
  /** 时间轴上选中的关键帧 id（单选） */
  const selectedKeyframeId = ref<string | null>(null)
  /** 关键帧姿态复制缓存（仅 position/rotation/scale） */
  const copiedKeyframePose = ref<CopiedKeyframePose | null>(null)
  /** 拖拽/伸缩 clip 终点对齐步长（秒）；`0` 表示不对齐 */
  const timelineSnapStepSec = ref(0.1)
  const selectedLayerId = ref<string | null>(null)
  const nextLayerSerial = ref(1)
  /**
   * 用户可选的导出格式（MP4 / WebM）；默认 MP4（H.264）。
   * 质量、编码器、码率由 {@link INTERNAL_EXPORT_QUALITY} 等内部预设统一管理，不对外暴露。
   */
  const exportFormat = ref<ExportFormatId>(DEFAULT_VIDEO_EXPORT_FORMAT)
  /** 用户可选的导出帧率（30 / 60）；默认 60。 */
  const exportFps = ref<ExportFpsId>(DEFAULT_VIDEO_EXPORT_FPS)
  const lastStubMessage = ref('')
  /**
   * 页面级浮层提示（如浏览器不支持 VP9 alpha 等需强提醒的场景）。
   * 由 `ImockupLayerToast` 通过 `Teleport` 渲染。
   */
  const editorLayerToast = shallowRef<EditorLayerToastState | null>(null)
  let editorLayerToastTimer: ReturnType<typeof setTimeout> | null = null
  /** 视频导出进度 0–1（仅导出视频时更新） */
  const exportVideoProgress = ref(0)
  /** 是否正在导出视频（用于 UI 禁用与取消） */
  const isExportingVideo = ref(false)
  /** 导出预检请求进行中（图片/视频共用，用于禁用按钮） */
  const isExportPreflight = ref(false)
  /** 模型是否正在加载（仅统计 GLTF/GLB） */
  const isModelLoading = ref(false)
  /** 模型加载进度 0–1 */
  const modelLoadProgress = ref(0)
  /** 并发 GLTF 加载数；只有全部归零后预览才允许启动。 */
  let pendingModelLoadCount = 0
  /** 加载期间收到的播放请求，全部模型就绪后自动兑现。 */
  let playbackPendingAfterModelsReady = false
  let exportVideoAbort: AbortController | null = null

  const viewportHandlers = ref<ViewportHandlers | null>(null)
  /** 视口变换工具；`null` 时仅画布指针用于普通命中（相机始终固定）。 */
  const viewportTransformTool = ref<ViewportTransformToolId | null>(null)

  const editorHistory = getMockupEditorHistory()
  const { session: editorAuthSession } = useEditorAuth()

  /**
   * @returns 当前全部图层的深拷贝（历史命令用）
   */
  function captureAnimationLayersForHistory(): AnimationLayer[] {
    return animationLayers.value.map(deepCloneLayer)
  }

  /**
   * 入栈一条已生效的撤销记录（undo/redo 应用过程中不入栈）。
   *
   * @param cmd - 命令；`null` 时忽略
   */
  function recordEditorHistory(cmd: EditorHistoryCommand | null): void {
    if (!cmd) return
    if (editorHistory.isApplying) return
    editorHistory.execute(cmd)
  }

  /**
   * 时间轴 / 视口手势结束后：若图层状态相对拖拽前有变化则记录。
   *
   * @param layerId - 图层 id
   * @param before - 手势前快照
   * @param after - 手势后快照
   * @param label - 命令标签
   */
  function recordEditorLayerGestureIfChanged(
    layerId: string,
    before: AnimationLayer,
    after: AnimationLayer,
    label: string,
  ): void {
    if (editorHistory.isApplying) return
    if (layerEditStateEquals(before, after)) return
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layerId, before, after, label))
  }

  const canUndoEditor = computed(() => {
    const rev = editorHistory.revision.value
    return rev >= 0 && editorHistory.canUndo()
  })

  const canRedoEditor = computed(() => {
    const rev = editorHistory.revision.value
    return rev >= 0 && editorHistory.canRedo()
  })

  /**
   * 撤销一步编辑器变更。
   */
  function undoEditor(): void {
    editorHistory.undo()
  }

  /**
   * 重做一步编辑器变更。
   */
  function redoEditor(): void {
    editorHistory.redo()
  }

  const selectedTemplate = computed<MockupDeviceTemplate | null>(() => {
    const id = selectedTemplateId.value
    return MOCKUP_DEVICE_TEMPLATES.find((d) => d.id === id) ?? null
  })

  const timelineMaxSeconds = computed(() => {
    const v = Number(totalDurationSeconds.value)
    if (!Number.isFinite(v)) return 10
    return Math.min(600, Math.max(1, v))
  })

  /**
   * 时间轴横向缩放系数：`1` 为整段适配容器；颗粒度拉满时单屏可视时长 ≈ 1s。
   * 受 `timelineMaxSeconds` 与「约 300px/秒」像素上限共同约束。
   */
  const timelineTrackWidthFactor = computed(() => {
    const g = timelineGranularity.value
    const maxSec = timelineMaxSeconds.value
    if (g <= 0) return 1
    const span = Math.max(0, maxSec - 1)
    const raw = 1 + (g / 100) * span
    const uncapped = Math.min(maxSec, Math.max(1, raw))
    const w = Math.max(320, timelineTrackClientWidthPx.value)
    const maxFactorForPxPerSec = (300 * maxSec) / w
    return Math.min(uncapped, maxFactorForPxPerSec)
  })

  /**
   * 标尺主/次刻度：随 `timelineTrackWidthFactor` 变化密度；最大缩放时主刻度不低于 1 秒。
   */
  const timelineRulerTickSet = computed<RulerTickSet>(() => {
    const max = timelineMaxSeconds.value
    const factor = Math.max(1, timelineTrackWidthFactor.value)
    const visibleSpan = max / factor
    return buildRulerTickSetMinMajorStep(max, visibleSpan, 1)
  })

  /** 兼容旧组件：仅主刻度秒数列表 */
  const timelineRulerTicks = computed(() => timelineRulerTickSet.value.majorSeconds)

  const sortedGradientStops = computed(() =>
    [...gradientStops.value].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id)),
  )

  const cssBackgroundGradient = computed(() => toCssLinearGradient(sortedGradientStops.value))

  const filteredDeviceTemplates = computed(() => {
    const cat = mockupCategory.value
    const inCategory = MOCKUP_DEVICE_TEMPLATES.filter((d) => d.category === cat)
    const q = deviceSearchQuery.value.trim().toLowerCase()
    if (!q) return inCategory
    return inCategory.filter(
      (d) => d.name.toLowerCase().includes(q) || `${d.width}`.includes(q) || `${d.height}`.includes(q),
    )
  })

  const formattedTime = computed(() => currentTimeSeconds.value.toFixed(2))

  const hasAnimationLayers = computed(() => animationLayers.value.length > 0)

  const activeLayer = computed<AnimationLayer | null>(() => {
    const id = selectedLayerId.value
    if (!id) return null
    return animationLayers.value.find((l) => l.id === id) ?? null
  })

  /** Inspector-facing pose at the playhead; evaluation never mutates the layer's base pose. */
  const activeLayerEvaluatedPose = computed<LayerPose | null>(() => {
    const layer = activeLayer.value
    if (!layer) return null
    return evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
  })

  /** 是否可删除当前选中的动画片段（时间轴已选中 clip） */
  const activeLayerCanRemoveSelectedClip = computed(
    () => selectedClipId.value != null && activeLayer.value != null,
  )
  const activeLayerLocked = computed(() => activeLayer.value?.locked === true)

  function setStubMessage(message: string): void {
    lastStubMessage.value = message
    if (import.meta.env.DEV) {
      console.info('[mockupEditor]', message)
    }
  }

  /**
   * 显示页面级浮层提示（自动消失，可手动关闭）。
   *
   * @param payload - 标题、正文、语义色；`durationMs` 默认约 9s
   */
  function showEditorLayerToast(
    payload: EditorLayerToastState & { durationMs?: number },
  ): void {
    const { durationMs = 9200, title, body, variant } = payload
    if (editorLayerToastTimer) {
      clearTimeout(editorLayerToastTimer)
      editorLayerToastTimer = null
    }
    editorLayerToast.value = { title, body, variant }
    editorLayerToastTimer = setTimeout(() => {
      editorLayerToast.value = null
      editorLayerToastTimer = null
    }, durationMs)
  }

  /** 立即关闭浮层提示并清除定时器 */
  function dismissEditorLayerToast(): void {
    if (editorLayerToastTimer) {
      clearTimeout(editorLayerToastTimer)
      editorLayerToastTimer = null
    }
    editorLayerToast.value = null
  }

  /**
   * @param layerId - 图层 id
   * @returns 图层是否处于锁定态
   */
  function isLayerLocked(layerId: string | null): boolean {
    if (!layerId) return false
    return animationLayers.value.find((layer) => layer.id === layerId)?.locked === true
  }

  /**
   * 编辑守卫：锁定层拒绝变更并给出统一提示。
   *
   * @param layer - 目标图层
   * @param actionLabel - 用户动作标签
   * @returns 可编辑时返回 `true`
   */
  function ensureLayerEditable(
    layer: AnimationLayer | null,
    actionKey: keyof typeof i18n.global.messages.value['zh-CN']['editorActions'],
  ): layer is AnimationLayer {
    if (!layer) return false
    if (!layer.locked) return true
    setStubMessage(
      tUi('editorMessages.lockedCannot', {
        action: tUi(`editorActions.${actionKey}`),
      }),
    )
    return false
  }

  /**
   * 片段几何变更后：规范为双端点关键帧并同步相邻片边界。
   *
   * @param layer - 动画图层
   */
  function afterClipGeometryChange(layer: AnimationLayer): void {
    for (const c of layer.clips) normalizeClipToEndpointPair(c)
    syncAdjacentClipBoundaries(layer)
  }

  /**
   * 按时间轴时刻刷新所有图层运行时可见状态。
   *
   * @param timeSec - 当前时间（秒）
   */
  function syncLayerRuntimeVisibilityAtTime(timeSec: number): void {
    const t = Number.isFinite(timeSec) ? timeSec : 0
    const duration = timelineMaxSeconds.value
    for (const layer of animationLayers.value) {
      const normalized = normalizeVisibilityWindow(
        layer.visibilityStartTimeSec,
        layer.visibilityEndTimeSec,
        duration,
      )
      /**
       * 仅在窗口归一化结果与当前值不同时再写回，避免无变化的 reactive 写入触发深度 watcher
       * （导出循环中频繁触发会与离线渲染时间窗错位，引发多层可见区间帧错乱）。
       */
      if (layer.visibilityStartTimeSec !== normalized.startSec) {
        layer.visibilityStartTimeSec = normalized.startSec
      }
      if (layer.visibilityEndTimeSec !== normalized.endSec) {
        layer.visibilityEndTimeSec = normalized.endSec
      }
      const nextVisible = t >= normalized.startSec && t <= normalized.endSec
      if (layer.visible !== nextVisible) {
        layer.visible = nextVisible
      }
    }
  }

  /**
   * 更新图层可见时间窗（秒）。
   *
   * @param layerId - 图层 id
   * @param startSec - 可见起点（秒）
   * @param endSec - 可见终点（秒）
   */
  function setLayerVisibilityWindow(layerId: string, startSec: number, endSec: number): void {
    const layer = animationLayers.value.find((item) => item.id === layerId)
    if (!layer) return
    const normalized = normalizeVisibilityWindow(startSec, endSec, timelineMaxSeconds.value)
    layer.visibilityStartTimeSec = normalized.startSec
    layer.visibilityEndTimeSec = normalized.endSec
    syncLayerRuntimeVisibilityAtTime(currentTimeSeconds.value)
  }

  /**
   * 总时长缩短时裁切所有图层 clip，并移除无效片段。
   */
  function clampAllClipsToTimelineMax(maxT: number): void {
    const cap = Math.min(600, Math.max(1, maxT))
    for (const layer of animationLayers.value) {
      const next: AnimationClip[] = []
      for (const c of layer.clips) {
        if (c.startSec >= cap - 1e-9) continue
        if (c.startSec + c.durationSec > cap + 1e-9) {
          c.durationSec = Math.max(MIN_CLIP_DURATION_SEC, cap - c.startSec)
        }
        if (c.durationSec >= MIN_CLIP_DURATION_SEC - 1e-9) next.push(c)
      }
      layer.clips = next
      afterClipGeometryChange(layer)
    }
  }

  function setViewportHandlers(handlers: ViewportHandlers | null): void {
    viewportHandlers.value = handlers
  }

  function viewportZoomIn(): void {
    viewportHandlers.value?.zoomIn()
  }

  function viewportZoomOut(): void {
    viewportHandlers.value?.zoomOut()
  }

  function viewportReset(): void {
    viewportHandlers.value?.resetView()
  }

  function viewportToggleRotate(): void {
    viewportHandlers.value?.toggleAutoRotate()
  }

  /**
   * 读取 UI 文案（兼容旧组件）。
   *
   * @param key - 文案 key
   * @param params - 插值参数
   */
  function tUi(key: string, params?: Record<string, string | number>): string {
    return params ? i18n.global.t(key, params) : i18n.global.t(key)
  }

  /**
   * 指定语言切换（非法值忽略）。用于菜单选择，便于扩展更多语言。
   *
   * @param next - 目标语言
   */
  function setLocale(next: UiLocale): void {
    if (!availableLocales.includes(next)) return
    if (locale.value === next) return
    locale.value = next
    i18n.global.locale.value = next
    if (typeof localStorage !== 'undefined') localStorage.setItem(LOCALE_STORAGE_KEY, next)
  }

  /**
   * 循环切换语言并同步 URL（避免静态 import `router` 造成与 Pinia 的循环依赖）。
   */
  function toggleLocale(): void {
    const currentIndex = availableLocales.indexOf(locale.value)
    const nextIndex = (currentIndex + 1) % availableLocales.length
    const next = availableLocales[nextIndex]!
    void import('@/router').then((m) => {
      const r = m.default
      const path = uiLocaleToEditorRouterPath(next)
      const cur = r.currentRoute.value
      if (cur.path !== path) void r.push({ path, query: cur.query, hash: cur.hash })
      else setLocale(next)
    })
  }

  /**
   * 默认/编辑模式切换（编辑模式用于聚焦画布区）。
   */
  function toggleWorkspaceMode(): void {
    workspaceMode.value = workspaceMode.value === 'default' ? 'edit' : 'default'
  }

  /**
   * 设置视口变换工具；传入 `null` 关闭工具（相机保持固定视角）。
   * 再次点击同一工具不会自动关闭（避免双击/重复 click 误关）；需显式传入 `null`。
   *
   * @param id - 工具 id 或 `null`
   */
  function setViewportTransformTool(id: ViewportTransformToolId | null): void {
    viewportTransformTool.value = id
  }

  /**
   * 设置**当前选中图层**的多图栈栅格切换过渡模式（每图层独立；同步写入 localStorage 供新建图层默认继承）。
   *
   * @param mode - 模式 id，与 `screenImageTransition` 常量一致
   */
  function setActiveLayerScreenImageTransitionMode(mode: ScreenImageTransitionModeId): void {
    const layer = activeLayer.value
    if (!layer) return
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return
    const before = deepCloneLayer(layer)
    layer.screenImageTransitionMode = mode
    writeStoredScreenImageTransitionModeDefault(mode)
    const after = deepCloneLayer(layer)
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Screen raster transition'),
    )
  }

  /**
   * 将当前选中图层的 `position` / `rotationDeg` / `scale` 写回播放头附近的片段端点关键帧（仅首尾/邻接边界）。
   * 供视口拖拽连续更新时节流调用，避免重复走单轴 setter。
   */
  function commitActiveLayerPoseAtPlayhead(): void {
    const layer = activeLayer.value
    if (!layer) return
    const pose: LayerPose = {
      position: cloneVec3(layer.position),
      rotationDeg: cloneVec3(layer.rotationDeg),
      scale: cloneVec3(layer.scale),
      materialOpacity: 1,
    }
    writeLayerPoseToClipBoundariesAtGlobalTime(layer, currentTimeSeconds.value, pose)
  }

  /**
   * 仅选中左侧库中的模板（不改变场景导出分辨率；加入画布请用「Add」或双击卡片）。
   *
   * @param id - {@link MOCKUP_DEVICE_TEMPLATES} 中的 id
   */
  function selectTemplate(id: string): void {
    if (!MOCKUP_DEVICE_TEMPLATES.some((d) => d.id === id)) return
    selectedTemplateId.value = id
  }

  /**
   * 将机型模板实例化到画布：创建动画图层并切换到 Mockup Controls。
   *
   * @param templateId - 指定模板 id；省略时使用当前 `selectedTemplateId`
   */
  function addSelectedDeviceToCanvas(templateId?: string | null): void {
    const beforeLayers = captureAnimationLayersForHistory()
    const beforeSelL = selectedLayerId.value
    const beforeSelC = selectedClipId.value
    const tidRaw = templateId ?? selectedTemplateId.value
    const tid =
      tidRaw && MOCKUP_DEVICE_TEMPLATES.some((d) => d.id === tidRaw)
        ? tidRaw
        : MOCKUP_DEVICE_TEMPLATES.find((d) => d.category === mockupCategory.value)?.id ??
          MOCKUP_DEVICE_TEMPLATES[0]?.id
    if (!tid) {
      setStubMessage('No device template selected.')
      return
    }
    const template = MOCKUP_DEVICE_TEMPLATES.find((d) => d.id === tid)
    const id = createLayerId()
    const serial = nextLayerSerial.value
    nextLayerSerial.value += 1
    const name = template ? `${template.name} ${serial}` : `Mockup ${serial}`
    animationLayers.value.push({
      id,
      name,
      locked: false,
      deviceTemplateId: tid,
      position: defaultVec3(),
      rotationDeg: defaultVec3(),
      scale: defaultScaleVec3(),
      ...defaultScreenMaterialFields(),
      ...defaultVisibilityFields(timelineMaxSeconds.value),
      screenImageTransitionMode: readStoredScreenImageTransitionModeDefault(),
      clips: [],
      ...(tid === 'text-plane'
        ? {
            textContent: 'Animock',
            textFontSize: 64,
            textColor: '#888888',
            textFontWeight: 'bold' as const,
            textAlign: 'center' as const,
          }
        : {}),
    })
    selectedLayerId.value = id
    syncLayerSelection()
    inspectorTab.value = 'controls'
    setStubMessage(`Added to canvas: ${name}`)
    const afterLayers = captureAnimationLayersForHistory()
    const afterSelL = selectedLayerId.value
    const afterSelC = selectedClipId.value
    recordEditorHistory(
      new EditorHistCmd.AnimationLayersStackCommand(
        beforeLayers,
        afterLayers,
        beforeSelL,
        beforeSelC,
        afterSelL,
        afterSelC,
        'Add layer',
      ),
    )
  }

  /** 设备卡片编辑：后续打开模板编辑器。 */
  function editDeviceTemplate(_id: string): void {
    setStubMessage('Edit device template (TODO).')
  }

  function setMockupCategory(id: MockupCategoryId): void {
    mockupCategory.value = id
    const list = MOCKUP_DEVICE_TEMPLATES.filter((d) => d.category === id)
    if (list.length === 0) {
      setStubMessage(`Category: ${id}（暂无设备）`)
      return
    }
    if (!list.some((d) => d.id === selectedTemplateId.value)) {
      selectedTemplateId.value = list[0]!.id
    }
    setStubMessage(`Category: ${id}`)
  }

  /**
   * 同步选中图层：无图层清空；仅 1 个则默认选中；多个时保留合法选中否则选第一个。
   */
  function syncLayerSelection(): void {
    const layers = animationLayers.value
    if (layers.length === 0) {
      selectedLayerId.value = null
      selectedClipId.value = null
      return
    }
    if (layers.length === 1) {
      selectedLayerId.value = layers[0]!.id
    } else {
      const ok = layers.some((l) => l.id === selectedLayerId.value)
      if (!ok) selectedLayerId.value = layers[0]!.id
    }
    const lyr = layers.find((l) => l.id === selectedLayerId.value)
    if (!lyr?.clips.some((c) => c.id === selectedClipId.value)) selectedClipId.value = null
    if (
      selectedKeyframeId.value &&
      !lyr?.clips.some(
        (c) => c.id === selectedClipId.value && c.keyframes.some((k) => k.id === selectedKeyframeId.value),
      )
    ) {
      selectedKeyframeId.value = null
    }
  }

  function selectAnimationLayer(layerId: string | null): void {
    if (layerId === null) {
      selectedLayerId.value = null
      selectedClipId.value = null
      selectedKeyframeId.value = null
      return
    }
    if (!animationLayers.value.some((l) => l.id === layerId)) return
    selectedLayerId.value = layerId
    const layer = animationLayers.value.find((l) => l.id === layerId)
    if (!layer?.clips.some((c) => c.id === selectedClipId.value)) {
      selectedClipId.value = null
      selectedKeyframeId.value = null
    }
  }

  /**
   * 选中时间轴片段并切到对应图层。
   *
   * @param clipId - 片段 id；`null` 清除选中
   */
  function selectAnimationClip(clipId: string | null): void {
    if (clipId === null) {
      selectedClipId.value = null
      selectedKeyframeId.value = null
      return
    }
    for (const l of animationLayers.value) {
      if (l.clips.some((c) => c.id === clipId)) {
        if (
          !l.clips.some(
            (c) => c.id === clipId && c.keyframes.some((k) => k.id === selectedKeyframeId.value),
          )
        ) {
          selectedKeyframeId.value = null
        }
        selectedLayerId.value = l.id
        selectedClipId.value = clipId
        return
      }
    }
  }

  /**
   * 选中单个关键帧；当 clip 不匹配或关键帧不存在时自动清空。
   *
   * @param clipId - 所属 clip id；传 `null` 清空
   * @param keyframeId - 关键帧 id；传 `null` 清空
   */
  function selectKeyframe(clipId: string | null, keyframeId: string | null): void {
    if (!clipId || !keyframeId) {
      selectedKeyframeId.value = null
      return
    }
    for (const layer of animationLayers.value) {
      const clip = layer.clips.find((c) => c.id === clipId)
      if (!clip) continue
      selectedLayerId.value = layer.id
      selectedClipId.value = clipId
      selectedKeyframeId.value = clip.keyframes.some((k) => k.id === keyframeId) ? keyframeId : null
      return
    }
    selectedKeyframeId.value = null
  }

  /**
   * 复制指定关键帧姿态到剪贴缓存（仅 position/rotation/scale）。
   *
   * @param clipId - 所属 clip id
   * @param keyframeId - 关键帧 id
   */
  function copyKeyframePose(clipId: string, keyframeId: string): void {
    for (const layer of animationLayers.value) {
      const clip = layer.clips.find((c) => c.id === clipId)
      if (!clip) continue
      const keyframe = clip.keyframes.find((k) => k.id === keyframeId)
      if (!keyframe) return
      copiedKeyframePose.value = {
        position: cloneVec3(keyframe.position),
        rotationDeg: cloneVec3(keyframe.rotationDeg),
        scale: cloneVec3(keyframe.scale),
        materialOpacity: clampMaterialOpacity(keyframe.materialOpacity),
      }
      return
    }
  }

  /**
   * 将已复制姿态粘贴到目标关键帧，并接入撤销重做。
   *
   * @param clipId - 目标 clip id
   * @param keyframeId - 目标关键帧 id
   */
  function pasteKeyframePose(clipId: string, keyframeId: string): void {
    const copied = copiedKeyframePose.value
    if (!copied) return
    for (const layer of animationLayers.value) {
      const clip = layer.clips.find((c) => c.id === clipId)
      if (!clip) continue
      if (!ensureLayerEditable(layer, 'pasteKeyframePose')) return
      const keyframe = clip.keyframes.find((k) => k.id === keyframeId)
      if (!keyframe) return
      const layerBefore = deepCloneLayer(layer)
      const beforeSelL = selectedLayerId.value
      const beforeSelC = selectedClipId.value
      keyframe.position = cloneVec3(copied.position)
      keyframe.rotationDeg = cloneVec3(copied.rotationDeg)
      keyframe.scale = cloneVec3(copied.scale)
      if (layer.deviceTemplateId === 'image-plane' || layer.deviceTemplateId === 'text-plane') {
        keyframe.materialOpacity = copied.materialOpacity
      }
      afterClipGeometryChange(layer)
      selectedLayerId.value = layer.id
      selectedClipId.value = clipId
      selectedKeyframeId.value = keyframeId
      const pose = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
      layer.position = cloneVec3(pose.position)
      layer.rotationDeg = cloneVec3(pose.rotationDeg)
      layer.scale = cloneVec3(pose.scale)
      const layerAfter = deepCloneLayer(layer)
      const afterSelL = selectedLayerId.value
      const afterSelC = selectedClipId.value
      recordEditorHistory(
        new EditorHistCmd.ReplaceLayerWithTimelineSelectionCommand(
          layer.id,
          layerBefore,
          layerAfter,
          beforeSelL,
          beforeSelC,
          afterSelL,
          afterSelC,
          'Paste keyframe pose',
        ),
      )
      return
    }
  }

  /**
   * 写入当前选中关键帧的材质不透明度（仅 `image-plane`）。
   *
   * @param value - 0～1
   */
  function setSelectedKeyframeMaterialOpacity(value: number): void {
    const layer = activeLayer.value
    if (!layer || (layer.deviceTemplateId !== 'image-plane' && layer.deviceTemplateId !== 'text-plane')) return
    if (!Number.isFinite(value)) return
    if (!ensureLayerEditable(layer, 'editKeyframeMaterialOpacity')) return
    const clipId = selectedClipId.value
    const keyframeId = selectedKeyframeId.value
    if (!clipId || !keyframeId) return
    const clip = layer.clips.find((c) => c.id === clipId)
    if (!clip) return
    const keyframe = clip.keyframes.find((k) => k.id === keyframeId)
    if (!keyframe) return
    const next = clampMaterialOpacity(value)
    const prev = clampMaterialOpacity(keyframe.materialOpacity)
    if (prev === next) return
    const before = deepCloneLayer(layer)
    keyframe.materialOpacity = next
    afterClipGeometryChange(layer)
    const after = deepCloneLayer(layer)
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Keyframe material opacity'),
    )
  }

  /**
   * 解析当前选中关键帧对应的全局时间（秒）；若选中无效则返回 `null`。
   */
  function resolveSelectedKeyframeGlobalTime(): number | null {
    const clipId = selectedClipId.value
    const keyframeId = selectedKeyframeId.value
    if (!clipId || !keyframeId) return null
    for (const layer of animationLayers.value) {
      const clip = layer.clips.find((c) => c.id === clipId)
      if (!clip) continue
      const kf = clip.keyframes.find((k) => k.id === keyframeId)
      if (!kf) return null
      return clip.startSec + kf.timeInClip
    }
    return null
  }

  /**
   * 播放头离开关键帧安全范围后自动清理关键帧选中态，避免出现“选中残留”。
   */
  function syncSelectedKeyframeByPlayhead(): void {
    if (!selectedKeyframeId.value) return
    const keyframeGlobalTime = resolveSelectedKeyframeGlobalTime()
    if (keyframeGlobalTime == null) {
      selectedKeyframeId.value = null
      return
    }
    const threshold = playheadSnapThresholdSec(timelineMaxSeconds.value)
    if (Math.abs(currentTimeSeconds.value - keyframeGlobalTime) > threshold) {
      selectedKeyframeId.value = null
    }
  }

  /**
   * 在播放头所在时间轴空档新增一条动画片段（受可见区间与空档双重限制；最短 1s）。
   */
  function addAnimationClipAtPlayhead(): void {
    const layer = activeLayer.value
    if (!layer) {
      setStubMessage('请先选中一个 Mockup 图层。')
      return
    }
    if (!ensureLayerEditable(layer, 'addClip')) return
    const layerBefore = deepCloneLayer(layer)
    const beforeSelL = selectedLayerId.value
    const beforeSelC = selectedClipId.value
    const t = Number(currentTimeSeconds.value.toFixed(4))
    const max = timelineMaxSeconds.value
    const clampedT = Math.min(max, Math.max(0, t))
    const visibilityStart = layer.visibilityStartTimeSec
    const visibilityEnd = layer.visibilityEndTimeSec
    if (clampedT < visibilityStart - 1e-9 || clampedT > visibilityEnd + 1e-9) {
      setStubMessage(tUi('editorMessages.playheadOutsideVisibilityWindow'))
      return
    }
    const visibilitySpan = visibilityEnd - visibilityStart
    if (visibilitySpan < MIN_CLIP_DURATION_SEC - 1e-9) {
      setStubMessage(tUi('editorMessages.visibilityWindowTooShortForClip'))
      return
    }
    if (isPlayheadInsideClipInterior(layer.clips, clampedT)) {
      setStubMessage('播放头位于片段内部时无法新增动画，请移到空白区域。')
      return
    }
    const preferred = Math.min(DEFAULT_NEW_CLIP_DURATION_SEC, visibilitySpan)
    const prop = proposeClipInGapAtTimeWithinWindow(
      layer.clips,
      clampedT,
      max,
      visibilityStart,
      visibilityEnd,
      preferred,
    )
    if (!prop) {
      setStubMessage(tUi('editorMessages.noGapInsideVisibilityWindow'))
      return
    }
    const prevPose = getPoseFromTouchingPreviousClipEnd(layer, prop.startSec)
    const poseFromLayer: LayerPose = {
      position: cloneVec3(layer.position),
      rotationDeg: cloneVec3(layer.rotationDeg),
      scale: cloneVec3(layer.scale),
      materialOpacity: 1,
    }
    const startPose = prevPose ?? poseFromLayer
    const endPose: LayerPose = {
      position: cloneVec3(startPose.position),
      rotationDeg: cloneVec3(startPose.rotationDeg),
      scale: cloneVec3(startPose.scale),
      materialOpacity: clampMaterialOpacity(startPose.materialOpacity),
    }
    const created: AnimationClip = {
      id: createClipId(),
      startSec: prop.startSec,
      durationSec: prop.durationSec,
      easingPreset: 'ease-in-out',
      keyframes: createTwoEndpointKeyframes(prop.durationSec, startPose, endPose),
    }
    layer.clips.push(created)
    afterClipGeometryChange(layer)
    selectedClipId.value = created.id
    const poseAfter = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
    layer.position = cloneVec3(poseAfter.position)
    layer.rotationDeg = cloneVec3(poseAfter.rotationDeg)
    layer.scale = cloneVec3(poseAfter.scale)
    setStubMessage(`已添加动画片段 ${created.startSec.toFixed(2)}s — ${(created.startSec + created.durationSec).toFixed(2)}s`)
    const layerAfter = deepCloneLayer(layer)
    const afterSelL = selectedLayerId.value
    const afterSelC = selectedClipId.value
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerWithTimelineSelectionCommand(
        layer.id,
        layerBefore,
        layerAfter,
        beforeSelL,
        beforeSelC,
        afterSelL,
        afterSelC,
        'Add clip',
      ),
    )
  }

  /**
   * 调整时间轴帧区域颗粒度（0–100）。
   *
   * @param value - 颗粒度；0 为整段总秒适配可视宽度
   */
  function setTimelineGranularity(value: number): void {
    const v = Math.round(Number(value))
    timelineGranularity.value = Math.min(100, Math.max(0, Number.isFinite(v) ? v : 0))
  }

  /** 颗粒度微调（用于 +/- 按钮）。 */
  function bumpTimelineGranularity(delta: number): void {
    setTimelineGranularity(timelineGranularity.value + delta)
  }

  /**
   * 设置 clip 拖拽/伸缩吸附步长（秒）；`0` 关闭吸附。
   *
   * @param sec - 步长，非有限或负数时视为 `0`
   */
  function setTimelineSnapStepSec(sec: number): void {
    const v = Number(sec)
    if (!Number.isFinite(v) || v <= 0) {
      timelineSnapStepSec.value = 0
      return
    }
    timelineSnapStepSec.value = Math.min(10, Math.max(0.01, v))
  }

  /**
   * 修改当前选中图层的显示名称。
   *
   * @param name - 非空时写入；空字符串则忽略
   */
  function setActiveLayerName(name: string): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'rename')) return
    const t = name.trim()
    if (!t) return
    const prev = layer.name
    if (prev === t) return
    layer.name = t
    recordEditorHistory(new EditorHistCmd.SetLayerNameCommand(layer.id, prev, t))
  }

  /**
   * 将当前选中图层的占位机型切换为另一款设备模板（画布 3D 比例随之更新）。
   *
   * @param templateId - `MOCKUP_DEVICE_TEMPLATES` 中的 id
   */
  function setActiveLayerDeviceTemplate(templateId: string): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'switchTemplate')) return
    if (!MOCKUP_DEVICE_TEMPLATES.some((d) => d.id === templateId)) return
    const prev = layer.deviceTemplateId
    if (prev === templateId) return
    layer.deviceTemplateId = templateId
    const t = MOCKUP_DEVICE_TEMPLATES.find((d) => d.id === templateId)
    setStubMessage(t ? `Device: ${t.name}` : 'Device template updated.')
    recordEditorHistory(new EditorHistCmd.SetLayerDeviceTemplateCommand(layer.id, prev, templateId))
  }

  /**
   * 按 id 删除动画图层（时间轴操作层与属性面板共用）。
   *
   * @param layerId - 图层 id
   */
  function removeMockupLayer(layerId: string): void {
    const hit = animationLayers.value.find((l) => l.id === layerId)
    if (!hit) return
    const beforeLayers = captureAnimationLayersForHistory()
    const beforeSelL = selectedLayerId.value
    const beforeSelC = selectedClipId.value
    if (hit.clips.some((c) => c.id === selectedClipId.value)) selectedClipId.value = null
    /** 撤销栈会持有快照中的 blob URL，此处不立即 revoke，交由历史 `dispose` 或后续覆盖逻辑释放 */
    animationLayers.value = animationLayers.value.filter((l) => l.id !== layerId)
    syncLayerSelection()
    setStubMessage('Mockup layer removed.')
    const afterLayers = captureAnimationLayersForHistory()
    const afterSelL = selectedLayerId.value
    const afterSelC = selectedClipId.value
    recordEditorHistory(
      new EditorHistCmd.AnimationLayersStackCommand(
        beforeLayers,
        afterLayers,
        beforeSelL,
        beforeSelC,
        afterSelL,
        afterSelC,
        'Remove layer',
      ),
    )
  }

  function setActiveLayerPosition(axis: keyof Vec3, value: number): void {
    const layer = activeLayer.value
    if (!Number.isFinite(value)) return
    if (!ensureLayerEditable(layer, 'editPosition')) return
    if (isPlayheadInsideClipInterior(layer.clips, currentTimeSeconds.value)) {
      setStubMessage('播放头位于片段内部时无法编辑变换，请移到片段首尾或空白区域。')
      return
    }
    const before = deepCloneLayer(layer)
    const evaluated = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
    layer.position = cloneVec3(evaluated.position)
    layer.rotationDeg = cloneVec3(evaluated.rotationDeg)
    layer.scale = cloneVec3(evaluated.scale)
    layer.position[axis] = value
    commitActiveLayerPoseAtPlayhead()
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Position'))
  }

  function setActiveLayerRotationDeg(axis: keyof Vec3, value: number): void {
    const layer = activeLayer.value
    if (!Number.isFinite(value)) return
    if (!ensureLayerEditable(layer, 'editRotation')) return
    if (isPlayheadInsideClipInterior(layer.clips, currentTimeSeconds.value)) {
      setStubMessage('播放头位于片段内部时无法编辑变换，请移到片段首尾或空白区域。')
      return
    }
    const before = deepCloneLayer(layer)
    const evaluated = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
    layer.position = cloneVec3(evaluated.position)
    layer.rotationDeg = cloneVec3(evaluated.rotationDeg)
    layer.scale = cloneVec3(evaluated.scale)
    layer.rotationDeg[axis] = value
    commitActiveLayerPoseAtPlayhead()
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Rotation'))
  }

  function setActiveLayerScale(axis: keyof Vec3, value: number): void {
    const layer = activeLayer.value
    if (!Number.isFinite(value)) return
    if (!ensureLayerEditable(layer, 'editScale')) return
    if (isPlayheadInsideClipInterior(layer.clips, currentTimeSeconds.value)) {
      setStubMessage('播放头位于片段内部时无法编辑变换，请移到片段首尾或空白区域。')
      return
    }
    const before = deepCloneLayer(layer)
    const evaluated = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
    layer.position = cloneVec3(evaluated.position)
    layer.rotationDeg = cloneVec3(evaluated.rotationDeg)
    layer.scale = cloneVec3(evaluated.scale)
    layer.scale[axis] = Math.max(0.01, value)
    commitActiveLayerPoseAtPlayhead()
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Scale'))
  }

  /**
   * 设置选中图层屏幕贴图；替换或清除时会 `revoke` 旧的对象 URL。
   *
   * @param fileName - 展示用文件名；清除时为 `null`
   * @param objectUrl - `URL.createObjectURL(file)`；清除时为 `null`
   * @param file - 原始文件；用于区分图片与视频（可选）
   */
  function setActiveLayerScreenMaterial(
    fileName: string | null,
    objectUrl: string | null,
    file?: File | null,
  ): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return
    const before = screenMaterialSnapshotFromLayer(layer)
    if (layer.screenTextureSrc) URL.revokeObjectURL(layer.screenTextureSrc)
    revokeAllScreenImageStackUrls(layer)
    layer.screenMaterialFileName = fileName
    layer.screenTextureSrc = objectUrl
    if (!objectUrl || !fileName) {
      layer.screenMediaType = 'none'
      layer.screenVideoTimelineOffsetSec = 0
    } else if (
      file &&
      ((typeof file.type === 'string' && file.type.startsWith('video/')) ||
        /\.mp4$/i.test(file.name))
    ) {
      layer.screenMediaType = 'video'
      layer.screenVideoTimelineOffsetSec = 0
    } else {
      layer.screenMediaType = 'image'
      layer.screenTextureSrc = null
      layer.screenImages = [
        {
          id: createScreenImageStackItemId(),
          fileName,
          textureSrc: objectUrl,
        },
      ]
    }
    const after = screenMaterialSnapshotFromLayer(layer)
    setStubMessage(fileName ? `Screen material: ${fileName}` : 'Screen material cleared.')
    recordEditorHistory(new EditorHistCmd.SetScreenMaterialCommand(layer.id, before, after))
  }

  /**
   * 在图片模式下向当前图层追加已通过校验的屏幕图（blob URL 已创建）；写入历史。
   */
  function appendActiveLayerScreenImageStackItems(
    items: ReadonlyArray<{ fileName: string; textureSrc: string }>,
  ): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return
    if (items.length === 0) return
    const before = screenMaterialSnapshotFromLayer(layer)
    for (const it of items) {
      layer.screenImages.push({
        id: createScreenImageStackItemId(),
        fileName: it.fileName,
        textureSrc: it.textureSrc,
      })
    }
    layer.screenMediaType = 'image'
    layer.screenTextureSrc = null
    layer.screenMaterialFileName = layer.screenImages[0]?.fileName ?? null
    const after = screenMaterialSnapshotFromLayer(layer)
    setStubMessage(`Screen images: ${layer.screenImages.length}`)
    recordEditorHistory(new EditorHistCmd.SetScreenMaterialCommand(layer.id, before, after))
  }

  /**
   * 从当前图层多图栈移除一项并 `revoke` 其 URL；修正关键帧引用。
   */
  function removeActiveLayerScreenImageById(imageId: string): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return
    const idx = layer.screenImages.findIndex((x) => x.id === imageId)
    if (idx < 0) return
    const before = screenMaterialSnapshotFromLayer(layer)
    const hit = layer.screenImages[idx]!
    if (hit.textureSrc) {
      try {
        URL.revokeObjectURL(hit.textureSrc)
      } catch {
        /* ignore */
      }
    }
    layer.screenImages.splice(idx, 1)
    scrubScreenImageIdFromLayerClips(layer, imageId)
    if (layer.screenImages.length === 0) {
      if (layer.screenTextureSrc) URL.revokeObjectURL(layer.screenTextureSrc)
      layer.screenTextureSrc = null
      layer.screenMaterialFileName = null
      layer.screenMediaType = 'none'
      layer.screenVideoTimelineOffsetSec = 0
    } else {
      layer.screenMaterialFileName = layer.screenImages[0]!.fileName
    }
    const after = screenMaterialSnapshotFromLayer(layer)
    recordEditorHistory(new EditorHistCmd.SetScreenMaterialCommand(layer.id, before, after))
  }

  /**
   * 将当前选中 clip 关键帧绑定到指定屏幕图 id（`null` 清除为继承默认）。
   */
  function setSelectedKeyframeScreenImageId(screenImageId: string | null): void {
    const layer = activeLayer.value
    const cid = selectedClipId.value
    const kid = selectedKeyframeId.value
    if (!layer || !cid || !kid) return
    if (!ensureLayerEditable(layer, 'editKeyframeScreenImage')) return
    const clip = layer.clips.find((c) => c.id === cid)
    const kf = clip?.keyframes.find((k) => k.id === kid)
    if (!clip || !kf) return
    const before = deepCloneLayer(layer)
    kf.screenImageId = screenImageId ?? undefined
    const after = deepCloneLayer(layer)
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Keyframe screen image'),
    )
  }

  /**
   * 一次性用多图栈替换当前图层屏幕媒体（会先释放视频与旧栈 URL）；用于「视频 → 多图」确认后单条历史。
   */
  function replaceActiveLayerScreenWithImageStack(
    items: ReadonlyArray<{ fileName: string; textureSrc: string }>,
  ): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return
    if (items.length === 0) return
    const before = screenMaterialSnapshotFromLayer(layer)
    if (layer.screenTextureSrc) URL.revokeObjectURL(layer.screenTextureSrc)
    revokeAllScreenImageStackUrls(layer)
    layer.screenImages = items.map((it) => ({
      id: createScreenImageStackItemId(),
      fileName: it.fileName,
      textureSrc: it.textureSrc,
    }))
    layer.screenMediaType = 'image'
    layer.screenTextureSrc = null
    layer.screenMaterialFileName = layer.screenImages[0]!.fileName
    layer.screenVideoTimelineOffsetSec = 0
    const after = screenMaterialSnapshotFromLayer(layer)
    recordEditorHistory(new EditorHistCmd.SetScreenMaterialCommand(layer.id, before, after))
  }

  async function buildValidatedRasterStackItemsForIngest(
    rasterFiles: readonly File[],
  ): Promise<Array<{ fileName: string; textureSrc: string }> | null> {
    const out: Array<{ fileName: string; textureSrc: string }> = []
    for (const f of rasterFiles) {
      if (isRejectedAnimatedRasterScreenFile(f)) {
        setStubMessage(tUi('inspector.controls.screenImageGifRejected'))
        for (const it of out) {
          try {
            URL.revokeObjectURL(it.textureSrc)
          } catch {
            /* ignore */
          }
        }
        return null
      }
      const prepared = await prepareScreenRasterFileForIngest(f)
      if (!prepared.ok) {
        for (const it of out) {
          try {
            URL.revokeObjectURL(it.textureSrc)
          } catch {
            /* ignore */
          }
        }
        setStubMessage(tUi('inspector.controls.screenImageLoadFailed'))
        return null
      }
      out.push({ fileName: f.name, textureSrc: URL.createObjectURL(prepared.file) })
    }
    return out
  }

  /**
   * 面板 / 视口拖放共用：多图追加、视频替换、互斥确认与像素校验。
   */
  async function ingestScreenMaterialFileList(files: readonly File[]): Promise<void> {
    const layer = activeLayer.value
    if (!layer || files.length === 0) return
    if (!ensureLayerEditable(layer, 'editScreenMaterial')) return

    const accepted = files.filter((f) => isScreenMaterialFileAcceptedForLayer(layer.deviceTemplateId, f))
    if (accepted.length === 0) {
      setStubMessage(tUi('editorMessages.viewportDropInvalidFile'))
      return
    }

    const videos = accepted.filter((f) => isVideoMp4ScreenMaterialFile(f))
    const rasters = accepted.filter((f) => isRasterImageScreenMaterialFile(f) && !isVideoMp4ScreenMaterialFile(f))

    if (videos.length > 0 && rasters.length > 0) {
      setStubMessage(tUi('editorMessages.viewportDropInvalidFile'))
      return
    }
    if (videos.length > 1) {
      setStubMessage(tUi('editorMessages.viewportDropInvalidFile'))
      return
    }

    if (videos.length === 1) {
      const vf = videos[0]!
      if (layer.screenImages.length > 0) {
        const ok = await requestScreenMediaOverwriteConfirm({
          title: tUi('inspector.controls.overwriteImagesWithVideoTitle'),
          body: tUi('inspector.controls.overwriteImagesWithVideoBody'),
          confirmLabel: tUi('inspector.controls.screenMediaOverwriteConfirm'),
          cancelLabel: tUi('inspector.controls.screenMediaOverwriteCancel'),
        })
        if (!ok) return
      }
      setActiveLayerScreenMaterial(vf.name, URL.createObjectURL(vf), vf)
      return
    }

    if (rasters.length === 0) return

    if (layer.screenMediaType === 'video' && layer.screenTextureSrc) {
      const ok = await requestScreenMediaOverwriteConfirm({
        title: tUi('inspector.controls.overwriteVideoWithImagesTitle'),
        body: tUi('inspector.controls.overwriteVideoWithImagesBody'),
        confirmLabel: tUi('inspector.controls.screenMediaOverwriteConfirm'),
        cancelLabel: tUi('inspector.controls.screenMediaOverwriteCancel'),
      })
      if (!ok) return
      const items = await buildValidatedRasterStackItemsForIngest(rasters)
      if (!items?.length) return
      if (items.length > MAX_SCREEN_IMAGE_COUNT) {
        for (const it of items) {
          try {
            URL.revokeObjectURL(it.textureSrc)
          } catch {
            /* ignore */
          }
        }
        setStubMessage(
          tUi('inspector.controls.screenImageMaxCount', {
            max: String(MAX_SCREEN_IMAGE_COUNT),
            room: '0',
          }),
        )
        return
      }
      replaceActiveLayerScreenWithImageStack(items)
      return
    }

    const room = MAX_SCREEN_IMAGE_COUNT - layer.screenImages.length
    if (rasters.length > room) {
      setStubMessage(
        tUi('inspector.controls.screenImageMaxCount', {
          max: String(MAX_SCREEN_IMAGE_COUNT),
          room: String(room),
        }),
      )
      return
    }

    const items = await buildValidatedRasterStackItemsForIngest(rasters)
    if (!items?.length) return
    appendActiveLayerScreenImageStackItems(items)
  }

  /**
   * 设置文字图层的文字内容（仅 `text-plane` 图层有效）。
   *
   * @param text - 新文字内容，最长 200 字符
   */
  function setActiveLayerTextContent(text: string): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editTextContent')) return
    if (layer.deviceTemplateId !== 'text-plane') return
    const normalized = text.replace(/\r\n?/g, '\n').slice(0, 200)
    if (layer.textContent === normalized) return
    const before = deepCloneLayer(layer)
    layer.textContent = normalized
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Text content'))
  }

  /**
   * 设置文字图层的字号（仅 `text-plane` 图层有效）。
   *
   * @param size - 字号（12–300 px）
   */
  function setActiveLayerTextFontSize(size: number): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editTextFontSize')) return
    if (layer.deviceTemplateId !== 'text-plane') return
    const clamped = Math.max(12, Math.min(300, Math.round(size)))
    if (!Number.isFinite(clamped)) return
    if (layer.textFontSize === clamped) return
    const before = deepCloneLayer(layer)
    layer.textFontSize = clamped
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Text font size'))
  }

  /**
   * 设置文字图层的颜色（仅 `text-plane` 图层有效）。
   *
   * @param color - CSS 颜色字符串（如 `'#ff0000'`）
   */
  function setActiveLayerTextColor(color: string): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editTextColor')) return
    if (layer.deviceTemplateId !== 'text-plane') return
    const trimmed = color.trim()
    if (!trimmed) return
    if (layer.textColor === trimmed) return
    const before = deepCloneLayer(layer)
    layer.textColor = trimmed
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Text color'))
  }

  /**
   * 设置文字图层的字重（仅 `text-plane` 图层有效）。
   *
   * @param weight - `normal` 或 `bold`
   */
  function setActiveLayerTextFontWeight(weight: 'normal' | 'bold'): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editTextFontWeight')) return
    if (layer.deviceTemplateId !== 'text-plane') return
    if (weight !== 'normal' && weight !== 'bold') return
    if ((layer.textFontWeight ?? 'bold') === weight) return
    const before = deepCloneLayer(layer)
    layer.textFontWeight = weight
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Text font weight'))
  }

  /**
   * 设置文字图层的对齐方式（仅 `text-plane` 图层有效）。
   *
   * @param align - `left` / `center` / `right`
   */
  function setActiveLayerTextAlign(align: 'left' | 'center' | 'right'): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'editTextAlign')) return
    if (layer.deviceTemplateId !== 'text-plane') return
    if (align !== 'left' && align !== 'center' && align !== 'right') return
    if ((layer.textAlign ?? 'center') === align) return
    const before = deepCloneLayer(layer)
    layer.textAlign = align
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Text align'))
  }

  /** 将选中图层位移/旋转归零，缩放归 1；若播放头在某片段内则重置该片段两端姿态。 */
  function resetActiveLayerTransform(): void {
    const layer = activeLayer.value
    if (!ensureLayerEditable(layer, 'resetTransform')) return
    const before = deepCloneLayer(layer)
    const t = currentTimeSeconds.value
    const clip = findClipContainingGlobalTime(layer.clips, t)
    if (clip) {
      resetClipEndpointsToDefaultPose(clip)
      afterClipGeometryChange(layer)
    } else {
      layer.position = defaultVec3()
      layer.rotationDeg = defaultVec3()
      layer.scale = defaultScaleVec3()
    }
    const pose = evaluateLayerPoseAtGlobalTime(layer, t)
    layer.position = cloneVec3(pose.position)
    layer.rotationDeg = cloneVec3(pose.rotationDeg)
    layer.scale = cloneVec3(pose.scale)
    setStubMessage('已重置变换。')
    const after = deepCloneLayer(layer)
    recordEditorHistory(new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, 'Reset transform'))
  }

  /** 删除当前选中的动画片段。 */
  function removeSelectedAnimationClip(): void {
    const layer = activeLayer.value
    const cid = selectedClipId.value
    if (!layer || !cid) {
      setStubMessage('请先在时间轴上选中要删除的动画片段。')
      return
    }
    if (!ensureLayerEditable(layer, 'removeClip')) return
    const layerBefore = deepCloneLayer(layer)
    const beforeSelL = selectedLayerId.value
    const beforeSelC = selectedClipId.value
    const beforeCount = layer.clips.length
    layer.clips = layer.clips.filter((c) => c.id !== cid)
    if (layer.clips.length === beforeCount) {
      setStubMessage('未找到选中的片段。')
      return
    }
    selectedClipId.value = null
    selectedKeyframeId.value = null
    afterClipGeometryChange(layer)
    const pose = evaluateLayerPoseAtGlobalTime(layer, currentTimeSeconds.value)
    layer.position = cloneVec3(pose.position)
    layer.rotationDeg = cloneVec3(pose.rotationDeg)
    layer.scale = cloneVec3(pose.scale)
    setStubMessage('已删除动画片段。')
    const layerAfter = deepCloneLayer(layer)
    const afterSelL = selectedLayerId.value
    const afterSelC = selectedClipId.value
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerWithTimelineSelectionCommand(
        layer.id,
        layerBefore,
        layerAfter,
        beforeSelL,
        beforeSelC,
        afterSelL,
        afterSelC,
        'Remove clip',
      ),
    )
  }

  /** 从时间轴移除当前选中 Mockup 图层。 */
  function removeActiveMockupLayer(): void {
    const id = selectedLayerId.value
    if (!id) return
    removeMockupLayer(id)
  }

  function getClipRef(
    layerId: string,
    clipId: string,
  ): { layer: AnimationLayer; clip: AnimationClip } | null {
    const layer = animationLayers.value.find((l) => l.id === layerId)
    if (!layer) return null
    const clip = layer.clips.find((c) => c.id === clipId)
    if (!clip) return null
    if (layer.locked) return null
    return { layer, clip }
  }

  /**
   * 切换图层锁定状态（进入历史栈，可撤销重做）。
   *
   * @param layerId - 图层 id
   */
  function toggleLayerLocked(layerId: string): void {
    const layer = animationLayers.value.find((item) => item.id === layerId)
    if (!layer) return
    setLayerLocked(layerId, !layer.locked)
  }

  /**
   * 设置图层锁定状态（进入历史栈，可撤销重做）。
   *
   * @param layerId - 图层 id
   * @param locked - 目标锁定状态
   */
  function setLayerLocked(layerId: string, locked: boolean): void {
    const layer = animationLayers.value.find((item) => item.id === layerId)
    if (!layer) return
    const next = locked === true
    if (layer.locked === next) return
    const before = deepCloneLayer(layer)
    layer.locked = next
    const after = deepCloneLayer(layer)
    recordEditorHistory(
      new EditorHistCmd.ReplaceLayerSnapshotCommand(layer.id, before, after, next ? 'Lock layer' : 'Unlock layer'),
    )
  }

  /**
   * 复制图层（包含全部 clip/keyframe），并重建所有层级 id 避免冲突。
   *
   * @param layerId - 源图层 id
   */
  function duplicateLayer(layerId: string): void {
    const source = animationLayers.value.find((layer) => layer.id === layerId)
    if (!source) return
    const beforeLayers = captureAnimationLayersForHistory()
    const beforeSelL = selectedLayerId.value
    const beforeSelC = selectedClipId.value
    const copied = deepCloneLayer(source)
    copied.id = createLayerId()
    copied.name = `${source.name} 副本`
    copied.clips = copied.clips.map((clip) => ({
      ...clip,
      id: createClipId(),
      keyframes: clip.keyframes.map((kf) => ({
        ...kf,
        id: createKeyframeId(),
      })),
    }))
    animationLayers.value.push(copied)
    selectedLayerId.value = copied.id
    selectedClipId.value = null
    selectedKeyframeId.value = null
    syncLayerSelection()
    const afterLayers = captureAnimationLayersForHistory()
    const afterSelL = selectedLayerId.value
    const afterSelC = selectedClipId.value
    recordEditorHistory(
      new EditorHistCmd.AnimationLayersStackCommand(
        beforeLayers,
        afterLayers,
        beforeSelL,
        beforeSelC,
        afterSelL,
        afterSelC,
        'Duplicate layer',
      ),
    )
  }

  /**
   * 平移 clip 起点（像素拖拽在组件内换算为秒后调用）。
   */
  function commitClipMove(layerId: string, clipId: string, newStartSec: number): void {
    const hit = getClipRef(layerId, clipId)
    if (!hit) return
    const max = timelineMaxSeconds.value
    let s = newStartSec
    const step = timelineSnapStepSec.value
    if (step > 0) s = snapSecondsToStep(s, step)
    hit.clip.startSec = clampClipStartNonOverlapping(hit.layer.clips, hit.clip, s, max)
    afterClipGeometryChange(hit.layer)
  }

  /**
   * 从左侧拖动手柄调整 clip。
   */
  function commitClipResizeLeft(layerId: string, clipId: string, newStartSec: number): void {
    const hit = getClipRef(layerId, clipId)
    if (!hit) return
    const max = timelineMaxSeconds.value
    let s = newStartSec
    const step = timelineSnapStepSec.value
    if (step > 0) s = snapSecondsToStep(s, step)
    const next = clampResizeClipLeft(hit.layer.clips, hit.clip, s, max)
    hit.clip.startSec = next.startSec
    hit.clip.durationSec = next.durationSec
    afterClipGeometryChange(hit.layer)
  }

  /**
   * 从右侧拖动手柄调整 clip 时长。
   */
  function commitClipResizeRight(layerId: string, clipId: string, newDurationSec: number): void {
    const hit = getClipRef(layerId, clipId)
    if (!hit) return
    const max = timelineMaxSeconds.value
    let d = newDurationSec
    const step = timelineSnapStepSec.value
    if (step > 0) d = snapSecondsToStep(d, step)
    hit.clip.durationSec = clampResizeClipRight(hit.layer.clips, hit.clip, d, max)
    afterClipGeometryChange(hit.layer)
  }

  function setCurrentTime(seconds: number): void {
    const max = timelineMaxSeconds.value
    const clamped = Math.min(max, Math.max(0, seconds))
    currentTimeSeconds.value = clamped
    syncLayerRuntimeVisibilityAtTime(clamped)
  }

  /**
   * 开始模型加载流程并重置进度（仅 GLTF/GLB）。
   */
  function startModelLoading(): void {
    if (pendingModelLoadCount === 0) modelLoadProgress.value = 0
    pendingModelLoadCount += 1
    isModelLoading.value = true
    if (isPlaying.value) {
      playbackPendingAfterModelsReady = true
      isPlaying.value = false
    }
  }

  /**
   * 基于 Loader `ProgressEvent` 更新模型加载进度。
   *
   * @param loaded - 已加载字节
   * @param total - 总字节（为 0 时保持当前进度）
   */
  function updateModelLoadProgress(loaded: number, total: number): void {
    if (!isModelLoading.value) return
    if (!Number.isFinite(total) || total <= 0) return
    const next = loaded / total
    modelLoadProgress.value = Math.min(1, Math.max(0, next))
  }

  /**
   * 结束模型加载流程并置满进度。
   */
  function finishModelLoading(): void {
    settleModelLoading(true)
  }

  /**
   * 模型加载失败，关闭加载态并重置进度。
   */
  function failModelLoading(): void {
    settleModelLoading(false)
  }

  function settleModelLoading(succeeded: boolean): void {
    pendingModelLoadCount = Math.max(0, pendingModelLoadCount - 1)
    if (pendingModelLoadCount > 0) return

    isModelLoading.value = false
    modelLoadProgress.value = succeeded ? 1 : 0
    if (!playbackPendingAfterModelsReady) return

    playbackPendingAfterModelsReady = false
    void nextTick(() => startPlaybackNow())
  }

  function setTotalDurationSeconds(seconds: number): void {
    const v = Math.min(600, Math.max(1, Math.round(seconds)))
    totalDurationSeconds.value = v
    clampAllClipsToTimelineMax(v)
    if (currentTimeSeconds.value > v) currentTimeSeconds.value = v
    syncLayerRuntimeVisibilityAtTime(currentTimeSeconds.value)
  }

  /**
   * 切换时间轴预览播放/暂停。
   * 若播放头已在时间轴末尾（含自然播放到结束），再次开始播放时先回到 0 秒，避免立刻再次触发「到达末尾」而无法重播。
   */
  function startPlaybackNow(): void {
    if (isPlaying.value) return
    if (isModelLoading.value) {
      playbackPendingAfterModelsReady = true
      return
    }

    const atTimelineEnd = currentTimeSeconds.value >= timelineMaxSeconds.value - 1e-5
    if (atTimelineEnd) {
      setCurrentTime(0)
      void nextTick(() => {
        if (isPlaying.value || currentTimeSeconds.value !== 0) return
        if (isModelLoading.value) {
          playbackPendingAfterModelsReady = true
          return
        }
        isPlaying.value = true
        setStubMessage('Playback started (preview).')
      })
      return
    }

    isPlaying.value = true
    setStubMessage('Playback started (preview).')
  }

  function togglePlayback(): void {
    if (isPlaying.value) {
      playbackPendingAfterModelsReady = false
      isPlaying.value = false
      setStubMessage('Playback paused.')
      return
    }

    if (isModelLoading.value) {
      playbackPendingAfterModelsReady = !playbackPendingAfterModelsReady
      setStubMessage(
        playbackPendingAfterModelsReady
          ? 'Playback will start when all models are ready.'
          : 'Playback cancelled.',
      )
      return
    }

    startPlaybackNow()
  }

  let playbackController: PlaybackController | null = null
  watch(isPlaying, (playing) => {
    /**
     * 任意切换都先 stop 上一个 controller：之前在 false→true 分支直接覆盖实例时，
     * 若上一次 step 内通过 `stopPlaying` 触发了 `isPlaying = false` 但 watch flush 尚未
     * 跑完（vue 异步 flush 合并了 false→true 两次写入），旧 controller 的 rAF 仍可能
     * 在新 controller 创建后短暂存活，造成「双驱动」累计 dt 直接跳到末尾然后再次停止，
     * 表现为「点击播放后无任何动画」。
     */
    playbackController?.stop()
    playbackController = null
    if (!playing) return
    playbackController = new PlaybackController(
      () => isPlaying.value,
      () => {
        isPlaying.value = false
      },
      () => timelineMaxSeconds.value,
      () => currentTimeSeconds.value,
      (sec) => {
        currentTimeSeconds.value = sec
      },
    )
    playbackController.start()
  })

  watch(
    () => [currentTimeSeconds.value, selectedClipId.value, selectedKeyframeId.value] as const,
    syncSelectedKeyframeByPlayhead,
    { immediate: true },
  )

  /**
   * 仅切换导出分辨率档（1080P / 2K / 4K）。
   * - 不修改预览 `sceneWidth` / `sceneHeight`。
   * - **不**改写 {@link activeRatioPreset}（与 Ratio Presets 并存、两组选中项正交）。
   */
  function applyResolutionPreset(preset: ResolutionPresetId): void {
    activeResolutionPreset.value = preset
  }

  /**
   * 设置预览画布比例与逻辑像素（与 {@link RATIO_PRESET_DIMENSIONS} 一致）。
   * - **不**改写 {@link activeResolutionPreset}（与 Resolution Presets 并存、两组选中项正交）。
   */
  function applyRatioPreset(ratio: RatioPresetId): void {
    activeRatioPreset.value = ratio
    const pair = RATIO_PRESET_DIMENSIONS[ratio]
    sceneWidth.value = pair[0]
    sceneHeight.value = pair[1]
  }

  /**
   * 按当前预览宽高与 {@link RATIO_PRESET_DIMENSIONS} 的宽高比对齐，刷新比例预设高亮（叉积相等）。
   * 仅更新 {@link activeRatioPreset}；**不**动导出分辨率档。手改宽高导致无法命中任一比例时为 `null`。
   */
  function reconcileActiveRatioPresetFromDimensions(): void {
    const w = sceneWidth.value
    const h = sceneHeight.value
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      activeRatioPreset.value = null
      return
    }
    const entries = Object.entries(RATIO_PRESET_DIMENSIONS) as [
      RatioPresetId,
      readonly [number, number],
    ][]
    const hit = entries.find(([, pair]) => {
      const pw = pair[0]
      const ph = pair[1]
      return Math.abs(w * ph - h * pw) < 1e-3
    })
    activeRatioPreset.value = hit ? hit[0] : null
  }

  watch([sceneWidth, sceneHeight], reconcileActiveRatioPresetFromDimensions, { immediate: true })

  /**
   * 当前「导出图 / 视频」像素尺寸：由导出分辨率档 + 比例预设（或手改预览宽高）推导，**不**等于中间预览画布。
   */
  function resolveActiveExportDimensions(): readonly [number, number] {
    const res = activeResolutionPreset.value
    const ratio = activeRatioPreset.value
    if (ratio) return getExportDimensions(res, ratio)
    return scalePreviewToExportDimensions(res, sceneWidth.value, sceneHeight.value)
  }

  const exportOutputWidth = computed(() => resolveActiveExportDimensions()[0])
  const exportOutputHeight = computed(() => resolveActiveExportDimensions()[1])

  function setBackgroundHex(hex: string): void {
    const prev = backgroundHex.value
    const next = normalizeHex7(hex)
    if (prev === next) return
    backgroundHex.value = next
    recordEditorHistory(new EditorHistCmd.SetBackgroundHexCommand(prev, next))
  }

  /** 应用渐变模板（会覆盖当前色标并记录选中预设）。 */
  function applyGradientPreset(presetId: string): void {
    const preset = GRADIENT_PRESET_LIST.find((p) => p.id === presetId)
    if (!preset) return
    gradientStops.value = seedToGradientStops(preset.stops)
    activeGradientPresetId.value = presetId
  }

  /**
   * 在渐变末端追加色标（新颜色排在当前渐变顺序最后），随后将所有色标 **均匀** 重排到 `0%…100%`。
   */
  function addGradientStop(): void {
    if (gradientStops.value.length >= 12) {
      setStubMessage('Maximum 12 gradient stops.')
      return
    }
    gradientStops.value.push({
      id: createStopId(),
      hex: '#ffffff',
      position: 100,
    })
    redistributeGradientStopsEvenly(gradientStops.value)
    activeGradientPresetId.value = null
  }

  /** 删除色标；至少保留 2 个；删除后对其余色标做均匀重排。 */
  function removeGradientStop(stopId: string): void {
    if (gradientStops.value.length <= 2) {
      setStubMessage('At least two gradient stops are required.')
      return
    }
    gradientStops.value = gradientStops.value.filter((s) => s.id !== stopId)
    redistributeGradientStopsEvenly(gradientStops.value)
    activeGradientPresetId.value = null
  }

  function setGradientStopHex(stopId: string, hex: string): void {
    const stop = gradientStops.value.find((s) => s.id === stopId)
    if (!stop) return
    const prev = stop.hex
    const next = normalizeHex7(hex)
    if (prev === next) return
    stop.hex = next
    activeGradientPresetId.value = null
    recordEditorHistory(new EditorHistCmd.SetGradientStopHexCommand(stopId, prev, next))
  }

  function setGradientStopPosition(stopId: string, position: number): void {
    const stop = gradientStops.value.find((s) => s.id === stopId)
    if (!stop) return
    const v = Number(position)
    stop.position = Math.min(100, Math.max(0, Number.isFinite(v) ? v : 0))
    activeGradientPresetId.value = null
  }

  function setBackgroundMode(mode: BackgroundModeId): void {
    const prev = backgroundMode.value
    if (prev === mode) return
    backgroundMode.value = mode
    recordEditorHistory(new EditorHistCmd.SetBackgroundModeCommand(prev, mode))
  }

  /**
   * 设置背景图片（平铺铺满视口）。
   *
   * - 传入 `null` 则清除；上一张 blob URL 会被 `revokeObjectURL`。
   * - **不**入历史栈（blob URL 生命周期与撤销语义会冲突）。
   *
   * @param fileName - 展示用文件名；清除时为 `null`
   * @param objectUrl - `URL.createObjectURL(file)`；清除时为 `null`
   */
  function setBackgroundImage(fileName: string | null, objectUrl: string | null): void {
    const prevUrl = backgroundImageSrc.value
    if (prevUrl && prevUrl !== objectUrl) {
      try {
        URL.revokeObjectURL(prevUrl)
      } catch {
        /* ignore */
      }
    }
    backgroundImageFileName.value = fileName
    backgroundImageSrc.value = objectUrl
    if (objectUrl && fileName) {
      setStubMessage(`Background image: ${fileName}`)
    } else {
      setStubMessage('Background image cleared.')
    }
  }

  /**
   * 清除当前背景图片并释放 blob URL。
   */
  function clearBackgroundImage(): void {
    setBackgroundImage(null, null)
  }

  /**
   * 切换视口灯光预设（HDR IBL + 辅助光由 Three 视口消费）。
   *
   * @param id - 预设 id；非法字符串时回退为默认棚拍预设
   */
  function setLightingPreset(id: string): void {
    lightingPresetId.value = isLightingPresetId(id) ? id : DEFAULT_LIGHTING_PRESET_ID
  }

  /**
   * 设置视口环境填充光倍率（半球光 + IBL 环境强度）。
   *
   * @param v - 原始数值，会被钳制到合法区间
   */
  function setViewportAmbientIntensityScale(v: number): void {
    viewportAmbientIntensityScale.value = clampViewportAmbientIntensityScale(v)
  }

  function buildEditorDraftAssets(): EditorDraftAssetInput[] {
    const assets: EditorDraftAssetInput[] = []

    if (backgroundImageSrc.value) {
      assets.push({
        assetKey: editorDraftBackgroundAssetKey(),
        objectUrl: backgroundImageSrc.value,
        fileName: backgroundImageFileName.value,
      })
    }

    for (const layer of animationLayers.value) {
      if (layer.screenMediaType === 'video' && layer.screenTextureSrc) {
        assets.push({
          assetKey: editorDraftLayerVideoAssetKey(layer.id),
          objectUrl: layer.screenTextureSrc,
          fileName: layer.screenMaterialFileName,
        })
        continue
      }

      if (layer.screenMediaType !== 'image') continue

      if (layer.screenImages.length > 0) {
        for (const image of layer.screenImages) {
          if (!image.textureSrc) continue
          assets.push({
            assetKey: editorDraftLayerScreenImageAssetKey(layer.id, image.id),
            objectUrl: image.textureSrc,
            fileName: image.fileName,
          })
        }
        continue
      }

      if (layer.screenTextureSrc) {
        assets.push({
          assetKey: editorDraftLayerLegacyImageAssetKey(layer.id),
          objectUrl: layer.screenTextureSrc,
          fileName: layer.screenMaterialFileName,
        })
      }
    }

    return assets
  }

  function restoreDraftAssets(assets?: ReadonlyMap<string, EditorDraftAssetRecord>): void {
    if (!assets || assets.size === 0) return

    if (backgroundMode.value === 'image' && backgroundImageFileName.value) {
      const backgroundAsset = assets.get(editorDraftBackgroundAssetKey())
      if (backgroundAsset) {
        backgroundImageSrc.value = URL.createObjectURL(backgroundAsset.blob)
      }
    }

    for (const layer of animationLayers.value) {
      if (layer.screenMediaType === 'video') {
        const videoAsset = assets.get(editorDraftLayerVideoAssetKey(layer.id))
        if (videoAsset) {
          layer.screenTextureSrc = URL.createObjectURL(videoAsset.blob)
        }
        continue
      }

      if (layer.screenMediaType !== 'image') continue

      const legacyAsset = assets.get(editorDraftLayerLegacyImageAssetKey(layer.id))
      for (const image of layer.screenImages) {
        const imageAsset = assets.get(editorDraftLayerScreenImageAssetKey(layer.id, image.id)) ?? legacyAsset
        if (!imageAsset) continue
        image.textureSrc = URL.createObjectURL(imageAsset.blob)
      }
    }
  }

  function buildProjectSnapshot(): ProjectSnapshot {
    return {
      version: PROJECT_SNAPSHOT_VERSION,
      sceneWidth: sceneWidth.value,
      sceneHeight: sceneHeight.value,
      exportResolutionPreset: activeResolutionPreset.value,
      backgroundMode: backgroundMode.value,
      backgroundHex: backgroundHex.value,
      backgroundImageFileName: backgroundImageFileName.value,
      gradientStops: gradientStops.value.map((s) => ({ ...s })),
      activeGradientPresetId: activeGradientPresetId.value,
      totalDurationSeconds: totalDurationSeconds.value,
      currentTimeSeconds: currentTimeSeconds.value,
      timelineGranularity: timelineGranularity.value,
      lightingPresetId: lightingPresetId.value,
      viewportAmbientIntensityScale: viewportAmbientIntensityScale.value,
      animationLayers: animationLayers.value.map(animationLayerToJson),
    }
  }

  /**
   * 将快照应用到 store（加载工程前会释放现有屏幕对象 URL）。
   *
   * @param data - 已规范化的 {@link ProjectSnapshot}（版本 2）
   */
  function applyProjectSnapshot(data: ProjectSnapshot): void {
    if (data.version !== PROJECT_SNAPSHOT_VERSION) {
      setStubMessage('工程版本不兼容，请使用当前应用打开。')
      return
    }
    editorHistory.clear()
    selectedClipId.value = null
    selectedKeyframeId.value = null
    copiedKeyframePose.value = null
    for (const l of animationLayers.value) {
      revokeAllLayerScreenBlobUrls(l)
    }
    sceneWidth.value = data.sceneWidth
    sceneHeight.value = data.sceneHeight
    activeResolutionPreset.value = data.exportResolutionPreset
    backgroundMode.value = data.backgroundMode
    backgroundHex.value = data.backgroundHex
    /** 背景图片 blob URL 无法持久化；仅恢复文件名作为占位，实际贴图需用户重选 */
    if (backgroundImageSrc.value) {
      try {
        URL.revokeObjectURL(backgroundImageSrc.value)
      } catch {
        /* ignore */
      }
    }
    backgroundImageFileName.value = data.backgroundImageFileName ?? null
    backgroundImageSrc.value = null
    gradientStops.value = data.gradientStops.map((s) => ({ ...s }))
    activeGradientPresetId.value = data.activeGradientPresetId
    totalDurationSeconds.value = data.totalDurationSeconds
    currentTimeSeconds.value = Math.min(
      timelineMaxSeconds.value,
      Math.max(0, data.currentTimeSeconds),
    )
    timelineGranularity.value = data.timelineGranularity
    lightingPresetId.value = isLightingPresetId(data.lightingPresetId)
      ? data.lightingPresetId
      : DEFAULT_LIGHTING_PRESET_ID
    viewportAmbientIntensityScale.value = clampViewportAmbientIntensityScale(
      data.viewportAmbientIntensityScale,
    )
    animationLayers.value = data.animationLayers.map(animationLayerFromJson)
    for (const layer of animationLayers.value) {
      const normalized = normalizeVisibilityWindow(
        layer.visibilityStartTimeSec,
        layer.visibilityEndTimeSec,
        timelineMaxSeconds.value,
      )
      layer.visibilityStartTimeSec = normalized.startSec
      layer.visibilityEndTimeSec = normalized.endSec
    }
    syncLayerRuntimeVisibilityAtTime(currentTimeSeconds.value)
    syncLayerSelection()
  }

  const editorDraftPersistenceEnabled = ref(false)

  const editorDraftAutoSave = createEditorDraftAutoSaveController({
    getSnapshot: buildProjectSnapshot,
    getDraftAssets: buildEditorDraftAssets,
    shouldSave: () =>
      editorDraftPersistenceEnabled.value && Boolean(editorAuthSession.value?.token),
    onPersistFailure: (n) => {
      if (n >= 3) {
        showEditorLayerToast({
          title: tUi('editorDraft.persistFailedTitle'),
          body: tUi('editorDraft.persistFailedBody'),
          variant: 'error',
          durationMs: 10000,
        })
      }
    },
  })

  let stopEditorDraftWatch: (() => void) | null = null

  /**
   * 开启本地草稿自动持久化（登录且草稿门禁处理完成后调用）。
   */
  function enableEditorDraftPersistence(): void {
    if (editorDraftPersistenceEnabled.value) return
    editorDraftPersistenceEnabled.value = true
    if (stopEditorDraftWatch != null) return
    stopEditorDraftWatch = watch(
      [
        sceneWidth,
        sceneHeight,
        activeResolutionPreset,
        backgroundMode,
        backgroundHex,
        backgroundImageFileName,
        gradientStops,
        activeGradientPresetId,
        totalDurationSeconds,
        currentTimeSeconds,
        timelineGranularity,
        lightingPresetId,
        viewportAmbientIntensityScale,
        animationLayers,
      ],
      () => {
        editorDraftAutoSave.markDirty()
      },
      { deep: true, flush: 'post' },
    )
  }

  /**
   * 应用本地自动缓存的工程快照（不经过文件选择器）。
   *
   * @param data - 已通过 {@link normalizeProjectSnapshot} 的快照
   */
  async function restoreEditorFromDraft(
    data: ProjectSnapshot,
    assets?: ReadonlyMap<string, EditorDraftAssetRecord>,
  ): Promise<void> {
    applyProjectSnapshot(data)
    restoreDraftAssets(assets)
    setStubMessage(tUi('editorDraft.restoredStub'))
  }

  onScopeDispose(() => {
    stopEditorDraftWatch?.()
    stopEditorDraftWatch = null
    editorDraftAutoSave.dispose()
  })

  const { openEditorAuthDialog } = useEditorAuthDialog()

  /**
   * 未登录则打开登录窗并阻断导出。
   *
   * @returns 已登录时为 `true`
   */
  function assertExportLoginGate(): boolean {
    const { session } = useEditorAuth()
    if (!session.value?.token) {
      setStubMessage(tUi('inspectorExport.loginRequired'))
      openEditorAuthDialog('login')
      return false
    }
    return true
  }

  /**
   * 组装导出预检 `meta_json`（不含大体积资源）。
   *
   * @param includeWatermark - 是否与客户端导出水印策略一致
   */
  function buildExportPreflightMetaJson(includeWatermark: boolean): Record<string, unknown> {
    return {
      background_mode: backgroundMode.value,
      background_hex: backgroundHex.value,
      resolution_preset: activeResolutionPreset.value,
      ratio_preset: activeRatioPreset.value,
      scene_width: sceneWidth.value,
      scene_height: sceneHeight.value,
      include_watermark: includeWatermark,
      layers: animationLayers.value.map((l) => ({
        id: l.id,
        deviceTemplateId: l.deviceTemplateId,
        visibilityStartTimeSec: l.visibilityStartTimeSec,
        visibilityEndTimeSec: l.visibilityEndTimeSec,
      })),
    }
  }

  async function exportImage(): Promise<void> {
    if (!assertExportLoginGate()) return
    if (isExportPreflight.value) {
      setStubMessage(tUi('inspectorExport.preflightInProgress'))
      return
    }
    const api = getViewportExportApi()
    if (!api) {
      setStubMessage('视口未就绪，无法导出图片。')
      return
    }
    const { session } = useEditorAuth()
    const includeWatermark = resolveExportIncludeWatermark(session.value?.isSubscribed)
    const [ew, eh] = resolveActiveExportDimensions()
    const w = Math.round(ew)
    const h = Math.round(eh)
    if (!Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
      setStubMessage('导出尺寸无效。')
      return
    }
    const token = session.value?.token
    if (!token) {
      setStubMessage(tUi('inspectorExport.loginRequired'))
      openEditorAuthDialog('login')
      return
    }
    isExportPreflight.value = true
    try {
      await postExportPreflight({
        token,
        export_type: 'image',
        width: w,
        height: h,
        snapshot_time_sec: currentTimeSeconds.value,
        meta_json: buildExportPreflightMetaJson(includeWatermark),
      })
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e)
      showEditorLayerToast({
        title: tUi('inspectorExport.preflightToastTitle'),
        body: tUi('inspectorExport.preflightToastBody', { reason }),
        variant: 'error',
        durationMs: 12000,
      })
      setStubMessage(tUi('inspectorExport.preflightFailed', { reason }))
      return
    } finally {
      isExportPreflight.value = false
    }
    /**
     * 与 {@link exportVideo}、`ViewportSceneAdapter` 一致：正式导出关闭选中框与叠加高亮，并按同一质量档调整渲染器（如阴影）。
     */
    api.beginExportVisualSession({ suppressEditorOverlay: true, quality: INTERNAL_EXPORT_QUALITY })
    api.beginSceneCanvasSize(w, h)
    try {
      await Promise.resolve(api.renderFrameAtTime(currentTimeSeconds.value))
      const canvas = api.getCanvas()
      if (!canvas) {
        setStubMessage('无法访问画布。')
        return
      }
      const exportCanvas = includeWatermark ? withWatermarkCanvas(canvas) : canvas
      const blob = await new Promise<Blob | null>((resolve) => {
        exportCanvas.toBlob((b) => resolve(b), 'image/png')
      })
      if (!blob) {
        setStubMessage('生成 PNG 失败。')
        return
      }
      triggerBlobDownload(blob, `animock-${Date.now()}.png`)
      setStubMessage(`已导出 PNG（${w}×${h}）。`)
    } catch (e) {
      setStubMessage(e instanceof Error ? e.message : '导出失败。')
    } finally {
      api.endSceneCanvasSize()
      api.endExportVisualSession()
    }
  }

  /**
   * 复制 WebGL 画布到 2D 画布并绘制水印（用于免费版导出限制）。
   *
   * @param source - WebGL 导出画布
   */
  function withWatermarkCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
    const w = source.width
    const h = source.height
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) return source
    ctx.drawImage(source, 0, 0)
    drawAnimockWatermark(ctx, w, h)
    return canvas
  }

  /**
   * 绘制导出水印（免费版限制）。
   *
   * @param ctx - 2D 上下文
   * @param w - 画布宽
   * @param h - 画布高
   */
  function drawAnimockWatermark(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const text = 'Animock'
    const base = Math.max(12, Math.round(Math.min(w, h) * 0.03))
    ctx.save()
    ctx.globalAlpha = 0.18
    ctx.fillStyle = '#0f172a'
    ctx.font = `700 ${base}px system-ui, -apple-system, Segoe UI, Roboto, Arial`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const step = base * 6
    ctx.translate(w / 2, h / 2)
    ctx.rotate(-Math.PI / 8)
    for (let y = -h; y <= h; y += step) {
      for (let x = -w; x <= w; x += step) {
        ctx.fillText(text, x, y)
      }
    }
    ctx.restore()
  }

  /**
   * 触发浏览器下载给定的视频 Blob。
   *
   * @param blob - 编码器输出
   * @param extension - 文件扩展名（`mp4` / `webm`）
   */
  function downloadVideoBlob(blob: Blob, extension: 'mp4' | 'webm'): void {
    triggerBlobDownload(blob, `animock-${Date.now()}.${extension}`)
  }

  /**
   * 解析用户输入并生成 `exportVideo` 所需的最小运行参数；校验失败时返回 `null` 并向 UI 报错。
   */
  function resolveExportVideoParams(): {
    fps: number
    outW: number
    outH: number
    duration: number
  } | null {
    const duration = timelineMaxSeconds.value
    const fps = Math.min(60, Math.max(1, Math.round(Number(exportFps.value))))
    const [rawW, rawH] = resolveActiveExportDimensions()
    const outW = Math.round(rawW)
    const outH = Math.round(rawH)
    if (!Number.isFinite(outW) || !Number.isFinite(outH) || outW < 1 || outH < 1) {
      setStubMessage('导出尺寸无效。')
      return null
    }
    return { fps, outW, outH, duration }
  }

  /**
   * 视频导出前置条件：视口就绪、无并发任务、透明背景下不选 MP4。
   *
   * @returns 通过时 `true`；否则已写入 {@link setStubMessage} 并返回 `false`
   */
  function assertExportVideoPreconditions(): boolean {
    if (!getViewportExportApi()) {
      setStubMessage('视口未就绪，无法导出视频。')
      return false
    }
    if (isExportPreflight.value) {
      setStubMessage(tUi('inspectorExport.preflightInProgress'))
      return false
    }
    if (isExportingVideo.value) {
      setStubMessage('已有导出任务进行中。')
      return false
    }
    if (backgroundMode.value === 'transparent' && exportFormat.value === 'mp4') {
      setStubMessage(tUi('inspectorExport.videoMp4TransparentBlocked'))
      return false
    }
    return true
  }

  /**
   * 处理 {@link resolveVideoEncoder} 探测阶段的失败：写入状态栏，并在特定原因下弹出浮层。
   *
   * @param e - 捕获的错误
   */
  function handleResolveVideoEncoderFailure(e: unknown): void {
    const msg =
      e instanceof VideoExportNotSupportedError
        ? e.message
        : e instanceof Error
          ? e.message
          : '视频导出探测失败。'
    if (e instanceof VideoExportNotSupportedError && e.code === 'mp4-unsupported') {
      showEditorLayerToast({
        title: tUi('inspectorExport.mp4UnsupportedTitle'),
        body: tUi('inspectorExport.mp4UnsupportedBody'),
        variant: 'error',
        durationMs: 12000,
      })
    }
    if (e instanceof VideoExportNotSupportedError && e.code === 'vp9-alpha-unavailable') {
      showEditorLayerToast({
        title: tUi('inspectorExport.vp9AlphaLayerTitle'),
        body: tUi('inspectorExport.vp9AlphaLayerBody'),
        variant: 'error',
      })
    }
    setStubMessage(msg)
  }

  /**
   * 使用离线逐帧渲染管线输出 MP4 或 WebM（帧索引驱动时间，非墙钟录屏）。
   *
   * - 格式由 {@link exportFormat} 决定；质量、编码器、码率由内部预设统一管理（{@link INTERNAL_EXPORT_QUALITY}）。
   * - MP4 失败时会自动回退到 WebM，并提示用户。
   */
  async function exportVideo(): Promise<void> {
    if (!assertExportLoginGate()) return
    if (!assertExportVideoPreconditions()) return

    const params = resolveExportVideoParams()
    if (!params) return
    const { fps, outW, outH, duration } = params

    const { session } = useEditorAuth()
    const includeWatermark = resolveExportIncludeWatermark(session.value?.isSubscribed)
    const token = session.value?.token
    if (!token) {
      setStubMessage(tUi('inspectorExport.loginRequired'))
      openEditorAuthDialog('login')
      return
    }
    isExportPreflight.value = true
    try {
      await postExportPreflight({
        token,
        export_type: 'video',
        width: outW,
        height: outH,
        video_format: exportFormat.value,
        video_fps: fps,
        duration_sec: duration,
        meta_json: buildExportPreflightMetaJson(includeWatermark),
      })
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e)
      showEditorLayerToast({
        title: tUi('inspectorExport.preflightToastTitle'),
        body: tUi('inspectorExport.preflightToastBody', { reason }),
        variant: 'error',
        durationMs: 12000,
      })
      setStubMessage(tUi('inspectorExport.preflightFailed', { reason }))
      return
    } finally {
      isExportPreflight.value = false
    }

    const requestedFormat = exportFormat.value
    const needsAlpha = backgroundMode.value === 'transparent'
    let resolved: Awaited<ReturnType<typeof resolveVideoEncoder>>
    try {
      resolved = await resolveVideoEncoder({
        format: requestedFormat,
        width: outW,
        height: outH,
        fps,
        quality: INTERNAL_EXPORT_QUALITY,
        alpha: needsAlpha,
      })
    } catch (e) {
      handleResolveVideoEncoderFailure(e)
      return
    }

    isPlaying.value = false
    const prevTime = currentTimeSeconds.value

    const ac = new AbortController()
    exportVideoAbort = ac
    isExportingVideo.value = true
    exportVideoProgress.value = 0

    const sceneAdapter = new ViewportSceneAdapter()
    const exportConfig: ExportRenderConfig = {
      durationSec: duration,
      fps,
      width: outW,
      height: outH,
      quality: INTERNAL_EXPORT_QUALITY,
      includeEditorOverlay: false,
      includeWatermark,
    }

    try {
      const result = await runOfflineRenderPipeline(
        ac.signal,
        exportConfig,
        sceneAdapter,
        resolved.sink,
        (p) => {
          exportVideoProgress.value =
            p.totalFrames > 0 ? Math.min(1, p.frameIndex / p.totalFrames) : 0
        },
      )

      if (ac.signal.aborted || result.status === 'cancelled') {
        setStubMessage('视频导出已取消。')
        return
      }
      if (result.status === 'error') {
        setStubMessage(result.error.message)
        return
      }

      downloadVideoBlob(result.blob, resolved.extension)
      const formatLabel = resolved.format.toUpperCase()
      setStubMessage(`已导出 ${formatLabel}（${outW}×${outH}，约 ${duration}s，${fps}fps）`)
    } catch (e) {
      setStubMessage(e instanceof Error ? e.message : '视频导出失败。')
    } finally {
      currentTimeSeconds.value = prevTime
      isExportingVideo.value = false
      exportVideoProgress.value = 0
      exportVideoAbort = null
    }
  }

  /** 取消进行中的视频导出 */
  function cancelExportVideo(): void {
    exportVideoAbort?.abort()
  }

  function saveProject(): void {
    const data = buildProjectSnapshot()
    const json = JSON.stringify(data, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    triggerBlobDownload(blob, `imockup-project-${Date.now()}.json`)
    clearEditorDraft()
    setStubMessage('工程 JSON 已下载（屏幕贴图需重新上传）。')
  }

  function loadProject(): void {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'application/json,.json'
    input.onchange = () => {
      const file = input.files?.[0]
      input.value = ''
      if (!file) return
      file
        .text()
        .then((text) => {
          loadProjectFromJsonText(text)
        })
        .catch(() => {
          setStubMessage('加载失败：文件无效。')
        })
    }
    input.click()
  }

  /**
   * 从 JSON 字符串解析并载入工程（与文件选择器共用校验逻辑）。
   *
   * @param text - 工程 JSON 文本
   * @returns 是否成功应用快照
   */
  function loadProjectFromJsonText(text: string): boolean {
    try {
      const raw = JSON.parse(text) as unknown
      return importProjectSnapshotFromUnknown(raw)
    } catch {
      setStubMessage('加载失败：文件无效。')
      return false
    }
  }

  /**
   * 将已解析的 JSON 值规范化为工程快照并应用到编辑器。
   *
   * @param raw - `JSON.parse` 结果或任意对象
   * @returns 是否成功应用快照
   */
  function importProjectSnapshotFromUnknown(raw: unknown): boolean {
    const normalized = normalizeProjectSnapshot(raw)
    if (!normalized) {
      setStubMessage('加载失败：工程版本无效或文件损坏。')
      return false
    }
    applyProjectSnapshot(normalized)
    clearEditorDraft()
    setStubMessage('工程已加载。')
    return true
  }

  function resetProject(): void {
    clearEditorDraft()
    editorHistory.clear()
    for (const l of animationLayers.value) {
      revokeAllLayerScreenBlobUrls(l)
    }
    applyRatioPreset('16:9')
    applyResolutionPreset('1080P')
    backgroundHex.value = '#ffffff'
    backgroundMode.value = 'color'
    if (backgroundImageSrc.value) {
      try {
        URL.revokeObjectURL(backgroundImageSrc.value)
      } catch {
        /* ignore */
      }
    }
    backgroundImageFileName.value = null
    backgroundImageSrc.value = null
    const preset = GRADIENT_PRESET_LIST.find((p) => p.id === 'studio-soft') ?? GRADIENT_PRESET_LIST[0]!
    gradientStops.value = seedToGradientStops(preset.stops)
    activeGradientPresetId.value = 'studio-soft'
    lightingPresetId.value = DEFAULT_LIGHTING_PRESET_ID
    viewportAmbientIntensityScale.value = VIEWPORT_AMBIENT_INTENSITY_SCALE_DEFAULT
    currentTimeSeconds.value = 0
    totalDurationSeconds.value = 10
    timelineGranularity.value = 0
    isPlaying.value = false
    inspectorTab.value = 'basic'
    deviceSearchQuery.value = ''
    mockupCategory.value = 'phone'
    selectedTemplateId.value =
      MOCKUP_DEVICE_TEMPLATES.find((d) => d.category === 'phone')?.id ??
      MOCKUP_DEVICE_TEMPLATES[0]?.id ??
      null
    animationLayers.value = []
    selectedLayerId.value = null
    selectedClipId.value = null
    selectedKeyframeId.value = null
    copiedKeyframePose.value = null
    nextLayerSerial.value = 1
    setStubMessage('Project reset to defaults.')
  }

  function setInspectorTab(tab: InspectorTabId): void {
    inspectorTab.value = tab
  }

  /**
   * 将当前选中 clip 的片段默认缓动设为 `preset`。
   *
   * @param preset - 合法预设 id
   */
  function setSelectedClipEasingPreset(preset: EasingPreset): void {
    if (!isEasingPreset(preset)) return
    const layer = activeLayer.value
    const cid = selectedClipId.value
    if (!layer || !cid) return
    if (!ensureLayerEditable(layer, 'editClipEasing')) return
    const c = layer.clips.find((x) => x.id === cid)
    if (!c) return
    const prev = c.easingPreset
    if (prev === preset) return
    c.easingPreset = preset
    recordEditorHistory(new EditorHistCmd.SetClipEasingPresetCommand(layer.id, cid, prev, preset))
  }

  /**
   * 由时间轴组件上报轨道宽度，用于限制最大横向缩放（约 300px/秒）。
   *
   * @param px - 像素宽度
   */
  function setTimelineTrackClientWidth(px: number): void {
    const v = Math.round(Number(px))
    if (!Number.isFinite(v) || v < 160) return
    timelineTrackClientWidthPx.value = Math.min(4000, v)
  }

  const mockupHistoryBridge: MockupHistoryBridge = {
    animationLayers,
    selectedLayerId,
    selectedClipId,
    currentTimeSeconds,
    backgroundHex,
    backgroundMode,
    gradientStops,
    activeGradientPresetId,
    getTimelineMaxSeconds: () => timelineMaxSeconds.value,
    afterClipGeometryChange,
    syncLayerSelection,
    removeLayer(layerId: string, revokeScreenTexture: boolean): void {
      const hit = animationLayers.value.find((l) => l.id === layerId)
      if (!hit) return
      if (revokeScreenTexture && hit.screenTextureSrc) {
        URL.revokeObjectURL(hit.screenTextureSrc)
      }
      animationLayers.value = animationLayers.value.filter((l) => l.id !== layerId)
      syncLayerSelection()
    },
    insertLayer(layer: AnimationLayer, index?: number): void {
      const copy = deepCloneLayer(layer)
      if (index == null || index < 0 || index > animationLayers.value.length) {
        animationLayers.value.push(copy)
      } else {
        animationLayers.value.splice(index, 0, copy)
      }
      syncLayerSelection()
    },
    replaceLayer(layerId: string, snapshot: AnimationLayer): void {
      const i = animationLayers.value.findIndex((l) => l.id === layerId)
      if (i < 0) return
      const prev = animationLayers.value[i]!
      const next = deepCloneLayer(snapshot)
      const prevUrl = prev.screenTextureSrc
      const nextUrl = next.screenTextureSrc
      if (prevUrl && prevUrl !== nextUrl) {
        const stillElsewhere = animationLayers.value.some(
          (l, j) =>
            j !== i && (l.screenTextureSrc === prevUrl || l.screenImages.some((si) => si.textureSrc === prevUrl)),
        )
        if (!stillElsewhere) {
          try {
            URL.revokeObjectURL(prevUrl)
          } catch {
            /* ignore */
          }
        }
      }
      for (const prevIt of prev.screenImages) {
        const u = prevIt.textureSrc
        if (!u) continue
        const stillElsewhere = animationLayers.value.some(
          (l, j) =>
            j !== i && (l.screenTextureSrc === u || l.screenImages.some((si) => si.textureSrc === u)),
        )
        if (!stillElsewhere) {
          try {
            URL.revokeObjectURL(u)
          } catch {
            /* ignore */
          }
        }
      }
      animationLayers.value[i] = next
      afterClipGeometryChange(animationLayers.value[i]!)
    },
  }
  setMockupHistoryBridge(mockupHistoryBridge)

  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.dataset.theme = 'light'
    root.style.colorScheme = 'light'
  }

  return {
    BG_COLOR_PRESETS,
    GRADIENT_PRESET_LIST,
    MOCKUP_DEVICE_TEMPLATES,
    RESOLUTION_PRESETS,
    RATIO_PRESETS,
    selectedTemplateId,
    mockupCategory,
    deviceSearchQuery,
    sceneWidth,
    sceneHeight,
    exportOutputWidth,
    exportOutputHeight,
    activeResolutionPreset,
    activeRatioPreset,
    backgroundMode,
    backgroundHex,
    backgroundImageFileName,
    backgroundImageSrc,
    gradientStops,
    activeGradientPresetId,
    lightingPresetId,
    viewportAmbientIntensityScale,
    sortedGradientStops,
    cssBackgroundGradient,
    currentTimeSeconds,
    setActiveLayerScreenImageTransitionMode,
    totalDurationSeconds,
    timelineGranularity,
    timelineTrackWidthFactor,
    isPlaying,
    inspectorTab,
    animationLayers,
    selectedClipId,
    selectedKeyframeId,
    copiedKeyframePose,
    selectedLayerId,
    hasAnimationLayers,
    activeLayer,
    activeLayerEvaluatedPose,
    activeLayerCanRemoveSelectedClip,
    activeLayerLocked,
    timelineRulerTickSet,
    timelineSnapStepSec,
    nextLayerSerial,
    syncLayerSelection,
    selectAnimationLayer,
    selectAnimationClip,
    selectKeyframe,
    copyKeyframePose,
    pasteKeyframePose,
    setSelectedKeyframeMaterialOpacity,
    setSelectedClipEasingPreset,
    setTimelineTrackClientWidth,
    commitClipMove,
    commitClipResizeLeft,
    commitClipResizeRight,
    addAnimationClipAtPlayhead,
    setTimelineGranularity,
    bumpTimelineGranularity,
    setTimelineSnapStepSec,
    setActiveLayerName,
    setActiveLayerDeviceTemplate,
    removeMockupLayer,
    isLayerLocked,
    toggleLayerLocked,
    setLayerLocked,
    duplicateLayer,
    setActiveLayerPosition,
    setActiveLayerRotationDeg,
    setActiveLayerScale,
    setActiveLayerScreenMaterial,
    appendActiveLayerScreenImageStackItems,
    removeActiveLayerScreenImageById,
    replaceActiveLayerScreenWithImageStack,
    ingestScreenMaterialFileList,
    setSelectedKeyframeScreenImageId,
    setActiveLayerTextContent,
    setActiveLayerTextFontSize,
    setActiveLayerTextColor,
    setActiveLayerTextFontWeight,
    setActiveLayerTextAlign,
    resetActiveLayerTransform,
    removeSelectedAnimationClip,
    removeActiveMockupLayer,
    exportFps,
    exportFormat,
    lastStubMessage,
    editorLayerToast,
    setStubMessage,
    dismissEditorLayerToast,
    canUndoEditor,
    canRedoEditor,
    undoEditor,
    redoEditor,
    recordEditorLayerGestureIfChanged,
    exportVideoProgress,
    isExportingVideo,
    isExportPreflight,
    isModelLoading,
    modelLoadProgress,
    showEditorLayerToast,
    selectedTemplate,
    timelineMaxSeconds,
    timelineRulerTicks,
    filteredDeviceTemplates,
    formattedTime,
    selectTemplate,
    addSelectedDeviceToCanvas,
    editDeviceTemplate,
    setMockupCategory,
    setCurrentTime,
    setLayerVisibilityWindow,
    syncLayerRuntimeVisibilityAtTime,
    startModelLoading,
    updateModelLoadProgress,
    finishModelLoading,
    failModelLoading,
    setTotalDurationSeconds,
    togglePlayback,
    applyResolutionPreset,
    applyRatioPreset,
    setBackgroundHex,
    setBackgroundMode,
    setBackgroundImage,
    clearBackgroundImage,
    setLightingPreset,
    setViewportAmbientIntensityScale,
    applyGradientPreset,
    addGradientStop,
    removeGradientStop,
    setGradientStopHex,
    setGradientStopPosition,
    exportImage,
    exportVideo,
    cancelExportVideo,
    saveProject,
    loadProject,
    loadProjectFromJsonText,
    importProjectSnapshotFromUnknown,
    enableEditorDraftPersistence,
    restoreEditorFromDraft,
    resetProject,
    setInspectorTab,
    setViewportHandlers,
    viewportZoomIn,
    viewportZoomOut,
    viewportReset,
    viewportToggleRotate,
    viewportTransformTool,
    setViewportTransformTool,
    commitActiveLayerPoseAtPlayhead,
    locale,
    availableLocales,
    workspaceMode,
    tUi,
    setLocale,
    toggleLocale,
    toggleWorkspaceMode,
  }
})
